<!-- ModuleTestsSection.vue -->
<template>
	<div
		v-show="isVisible"
		class="mts-container"
		data-component="module-tests-section"
	>
		<!-- Tabs Navigation -->
		<nav v-if="!hideTabs" class="mts-tabs" role="tablist">
			<button
				v-for="tab in visibleTabs"
				:key="tab.id"
				:class="[
					'mts-tabs__item',
					{ 'mts-tabs__item--active': activeTab === tab.id },
				]"
				:aria-selected="activeTab === tab.id"
				role="tab"
				type="button"
				@click="handleTabChange(tab.id)"
			>
				{{ tab.label }}
			</button>
		</nav>

		<template v-if="!hideTabs">
		<!-- Content Area -->
		<div class="mts-content">
			<div class="mts-content__bg"></div>

			<!-- Header Section -->
			<header class="mts-header">
				<h2 class="mts-header__title">{{ sectionTitle }}</h2>
				<button
					v-if="showCreateButton"
					class="mts-header__create-btn"
					type="button"
					@click="handleCreateItem"
				>
					<div class="mts-header__create-bg"></div>
					<span class="mts-header__create-text">{{ createButtonText }}</span>
				</button>
			</header>

			<!-- Materials Slider (опубликованные материалы) -->
			<div
				v-if="activeTab !== 'settings' && publishedMaterials.length > 0"
				class="mts-materials"
			>
				<MaterialsSlider
					:items="publishedMaterials"
					:items-per-page="12"
					:columns="4"
				>
					<template #card="{ item }">
						<div :data-material-id="item.id">
							<MaterialCard
								:key="item.id"
								:material-id="item.id"
								:type="activeTab"
								:title="item.title"
								:auto-publish="item.autoPublish"
								:duration="item.duration"
								:questions-count="item.questionsCount"
								:passing-score="item.passingScore"
								:attempts="item.attempts"
								:lesson-type="item.lessonType"
								:due-date="item.dueDate"
								:due-time="item.dueTime"
								:is-draft="item.isDraft"
								:is-hidden="item.isHidden"
								:scheduled-publish-at="item.scheduledPublishAt"
								@edit="handleEditMaterial"
								@toggle-visibility="handleToggleVisibility"
								@delete="handleDeleteMaterial"
								@preview-intent="handlePreviewIntent"
								@preview="handlePreviewMaterial"
							/>
						</div>
					</template>
				</MaterialsSlider>
			</div>

			<!-- Drafts Section (показывается только если есть черновики) -->
			<section
				v-if="activeTab !== 'settings' && currentDrafts.length > 0"
				class="mts-drafts"
			>
				<div class="mts-drafts__header">
					<h3 class="mts-drafts__title">Черновики</h3>
					<div class="mts-drafts__divider"></div>
				</div>

				<!-- Drafts Slider -->
				<div class="mts-drafts__slider">
					<MaterialsSlider
						:items="currentDrafts"
						:items-per-page="12"
						:columns="4"
					>
						<template #card="{ item }">
							<MaterialCard
								:key="item.id"
								:material-id="item.id"
								:type="activeTab"
								:title="item.title"
								:auto-publish="item.autoPublish"
								:duration="item.duration"
								:questions-count="item.questionsCount"
								:passing-score="item.passingScore"
								:attempts="item.attempts"
								:lesson-type="item.lessonType"
								:due-date="item.dueDate"
								:due-time="item.dueTime"
								:is-draft="true"
								:is-hidden="false"
								@edit="handleEditMaterial"
								@toggle-visibility="handleToggleVisibility"
								@delete="handleDeleteMaterial"
								@preview-intent="handlePreviewIntent"
								@preview="handlePreviewMaterial"
							/>
						</template>
					</MaterialsSlider>
				</div>
			</section>

			<div v-if="activeTab === 'settings'" class="mts-settings">
				<slot name="settings"></slot>
			</div>
		</div>

		<!-- Test Creation Form (вне блока .mts-content) -->
		<div
			v-show="activeTab === 'tests' && showTestCreation && (!editingMaterial || editingMaterialType === 'tests')"
			class="mts-test-creation"
		>
			<TestCreationForm
				:key="testFormKey"
				:current-module-id="moduleId"
				:editing-test="editingMaterial"
				@save-test="handleSaveTest"
				@save-draft="handleSaveDraft"
				@cancel="handleCancelTestCreation"
			/>
		</div>

		<!-- Lesson Creation Form -->
		<div
			v-show="activeTab === 'lessons' && showLessonCreation"
			class="mts-lesson-creation"
		>
			<LessonCreationForm
				:key="lessonFormKey"
				:current-module-id="currentModuleId"
				:available-modules="modules"
				:editing-lesson="editingLesson"
				@save-lesson="handleSaveLesson"
				@save-draft="handleSaveDraft"
				@cancel="handleCancelLessonCreation"
			/>
		</div>

		<!-- Practice Creation Form -->
		<div
			v-show="activeTab === 'practice' && showPracticeCreation"
			class="mts-practice-creation"
		>
			<PracticeCreationForm
				:key="practiceFormKey"
				:current-module-id="currentModuleId"
				:available-modules="modules"
				:editing-practice="editingPractice"
				@save-practice="handleSavePractice"
				@save-draft="handleSaveDraft"
				@cancel="handleCancelPracticeCreation"
			/>
		</div>

		<!-- Homework Creation Form -->
		<div
			v-show="activeTab === 'homework' && showHomeworkCreation"
			class="mts-homework-creation"
		>
			<HomeworkCreationForm
				:key="homeworkFormKey"
				:current-module-id="currentModuleId"
				:available-modules="modules"
				:editing-homework="editingHomework"
				@save-homework="handleSaveHomework"
				@save-draft="handleSaveDraft"
				@cancel="handleCancelHomeworkCreation"
			/>
		</div>

		<!-- Edit Material Modal -->
		<EditMaterialModal
			:is-open="showEditModal"
			:material="editingMaterial"
			:material-type="safeMaterialTypeForModal"
			:current-module-id="currentModuleId"
			:available-modules="modules"
			@close="handleCloseEditModal"
			@save="handleSaveEditedMaterial"
			@save-draft="handleSaveEditedDraft"
			@preview-intent="handlePreviewIntent"
			@preview="handlePreviewMaterial"
		/>
		</template>
	</div>
</template>

<script>
import TestCreationForm from '../TestCreationForm.vue'
import LessonCreationForm from '../LessonCreationForm.vue'
import PracticeCreationForm from '../PracticeCreationForm.vue'
import HomeworkCreationForm from '../HomeworkCreationForm.vue'
import MaterialCard from './MaterialCard.vue'
import MaterialsSlider from '@/components/MaterialsSlider.vue'
import EditMaterialModal from '@/components/EditMaterialModal.vue'
import { openInternalRouteInContext } from '@/utils/bitrixInternalNavigation'

export default {
	name: 'ModuleTestsSection',

	components: {
		TestCreationForm,
		LessonCreationForm,
		PracticeCreationForm,
		HomeworkCreationForm,
		MaterialCard,
		MaterialsSlider,
		EditMaterialModal,
	},

	props: {
		moduleId: {
			type: [Number, String],
			default: null,
		},
		courseId: {
			type: [Number, String],
			default: null,
		},
		// Локальный модуль для работы без БД
		localModule: {
			type: Object,
			default: null,
		},
		// Режим работы: true = локально, false = с БД
		isLocalMode: {
			type: Boolean,
			default: false,
		},
		showSettingsTab: {
			type: Boolean,
			default: false,
		},
		// Начальные материалы для локального режима
		initialMaterials: {
			type: Object,
			default: () => ({
				lessons: [],
				tests: [],
				practice: [],
				homework: [],
			}),
		},
		hideTabs: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			activeTab: 'lessons',
			lastNonSettingsTab: 'lessons',
			tabs: [
				{ id: 'lessons', label: 'Уроки' },
				{ id: 'practice', label: 'Практические задания' },
				{ id: 'homework', label: 'Домашние задания' },
				{ id: 'tests', label: 'Тесты' },
				{ id: 'settings', label: 'Настройки' },
			],

			lessons: [],
			practices: [],
			homeworks: [],
			tests: [],
			drafts: [],

			showTestCreation: false,
			showLessonCreation: false,
			showPracticeCreation: false,
			showHomeworkCreation: false,

			showEditModal: false,
			editingMaterial: null,
			editingMaterialType: null,
			editingMaterialIndex: null, //  ДОБАВЛЕНО для отслеживания индекса

			//  Key для перемонтирования форм при создании/редактировании
			lessonFormKey: 0,
			testFormKey: 0,
			practiceFormKey: 0,
			homeworkFormKey: 0,

			//  Информация о текущем модуле
			currentModule: null,
		}
	},

	computed: {
		isVisible() {
			return true
		},

		visibleTabs() {
			return this.showSettingsTab
				? this.tabs
				: this.tabs.filter(tab => tab.id !== 'settings')
		},

		sectionTitle() {
			const titleMap = {
				lessons: 'Уроки модуля',
				practice: 'Практические задания модуля',
				homework: 'Домашние задания модуля',
				tests: 'Тесты модуля',
				settings: 'Настройки модуля',
			}
			return titleMap[this.activeTab] || 'Контент модуля'
		},

		createButtonText() {
			const buttonTextMap = {
				lessons: 'Создать урок',
				practice: 'Создать практическое задание',
				homework: 'Создать домашнее задание',
				tests: 'Создать тест',
			}
			return buttonTextMap[this.activeTab] || 'Создать'
		},

		showCreateButton() {
			return this.activeTab !== 'settings'
		},

		currentMaterials() {
			//  В локальном режиме используем локальные массивы (this.lessons, this.tests, и т.д.)
			// Эти массивы заполняются из initialMaterials в mounted() и обновляются при создании/редактировании
			const materialsMap = {
				lessons: this.lessons,
				practice: this.practices,
				homework: this.homeworks,
				tests: this.tests,
			}
			const rawMaterials = materialsMap[this.activeTab] || []
			
			console.log(`[currentMaterials] Активная вкладка: ${this.activeTab}, Материалов: ${rawMaterials.length}`)
			
			//  Преобразуем материалы для отображения (добавляем lessonType и другие поля)
			const transformed = this.transformMaterialsForDisplay(rawMaterials, this.activeTab)
			
			console.log(`[currentMaterials] После трансформации: ${transformed.length}`, transformed)
			
			return transformed
		},

		publishedMaterials() {
			return this.currentMaterials.filter(m => !m.isDraft)
		},

		currentDrafts() {
			return this.currentMaterials.filter(m => m.isDraft)
		},

		// Computed свойства для передачи в формы создания
		currentModuleId() {
			return this.moduleId
		},

		modules() {
			// Возвращаем массив с текущим модулем
			if (this.currentModule) {
				return [
					{
						id: this.currentModule.id,
						title: this.currentModule.title || this.currentModule.name,
						name: this.currentModule.title || this.currentModule.name,
					},
				]
			}
			return this.moduleId
				? [
						{
							id: this.moduleId,
							title: '',
							name: '',
						},
				  ]
				: []
		},

		editingLesson() {
			return this.editingMaterialType === 'lessons'
				? this.editingMaterial
				: null
		},

		editingPractice() {
			return this.editingMaterialType === 'practice'
				? this.editingMaterial
				: null
		},

		editingHomework() {
			return this.editingMaterialType === 'homework'
				? this.editingMaterial
				: null
		},

		safeMaterialTypeForModal() {
			const rawType = this.editingMaterialType || this.activeTab
			const allowedTypes = ['lessons', 'tests', 'practice', 'homework']
			return allowedTypes.includes(rawType) ? rawType : 'lessons'
		},
	},

	async mounted() {
		if (!this.hideTabs) {
			this.restoreActiveTab()
		}
		this.restoreFormStates()
		if (this.hideTabs) {
			if (this.activeTab && this.activeTab !== 'settings') {
				this.lastNonSettingsTab = this.activeTab
			}
			this.activeTab = 'settings'
		}
		//  Загружаем данные только если НЕ в локальном режиме
		if (!this.isLocalMode) {
			await this.loadAllMaterialsFromDB()
		} else {
			//  В локальном режиме загружаем материалы из initialMaterials
			console.log(
				'📦 Загрузка материалов из initialMaterials:',
				this.initialMaterials
			)
			this.lessons = [...(this.initialMaterials.lessons || [])]
			this.tests = [...(this.initialMaterials.tests || [])]
			this.practices = [...(this.initialMaterials.practice || [])]
			this.homeworks = [...(this.initialMaterials.homework || [])]
			console.log(' Материалы загружены в локальные массивы:', {
				lessons: this.lessons.length,
				tests: this.tests.length,
				practices: this.practices.length,
				homeworks: this.homeworks.length,
			})
		}
	},

	watch: {
		hideTabs(newValue, oldValue) {
			if (newValue) {
				if (this.activeTab && this.activeTab !== 'settings') {
					this.lastNonSettingsTab = this.activeTab
				}
				this.activeTab = 'settings'
				return
			}

			if (oldValue && !newValue) {
				this.activeTab = this.lastNonSettingsTab || 'lessons'
			}
		},

		activeTab(newValue) {
			if (!this.hideTabs) {
				this.saveActiveTab(newValue)
			}
			if (newValue && newValue !== 'settings') {
				this.lastNonSettingsTab = newValue
			}
		},

		async moduleId(newValue, oldValue) {
			if (newValue && newValue !== oldValue) {
				console.log(`🔄 moduleId изменился: ${oldValue} → ${newValue}`)
				if (!this.isLocalMode) {
					await this.loadAllMaterialsFromDB()
				}
			}
		},

		initialMaterials: {
			handler(newValue) {
				if (this.isLocalMode && newValue) {
					console.log(
						'🔄 initialMaterials изменились, обновляем локальные массивы:',
						newValue
					)

					//  ДОБАВЛЕНО: Сохраняем материалы с локальными изменениями
					const localChangedLessons = this.lessons.filter(
						l => l.hasLocalChanges
					)
					const localChangedTests = this.tests.filter(t => t.hasLocalChanges)
					const localChangedPractices = this.practices.filter(
						p => p.hasLocalChanges
					)
					const localChangedHomeworks = this.homeworks.filter(
						h => h.hasLocalChanges
					)

					console.log('💾 Сохранено материалов с локальными изменениями:', {
						lessons: localChangedLessons.length,
						tests: localChangedTests.length,
						practices: localChangedPractices.length,
						homeworks: localChangedHomeworks.length,
					})

					// Обновляем массивы из initialMaterials - УРОКИ
					this.lessons = (newValue.lessons || []).map(lesson => ({
						...lesson,
						fullData: {
							id: lesson.id,
							title: lesson.title,
							type: lesson.lesson_type || 'video',
							content: lesson.article_content || lesson.content || '',
							description: lesson.description || '',
							duration: lesson.duration_minutes || 30,
							videoUrl: lesson.video_url || '',
							videoCoverUrl:
								lesson.cover_image_url || lesson.cover_image || '',
							cover_image_url:
								lesson.cover_image_url || lesson.cover_image || null,
							docFile: null,
							materials: lesson.materials || [],
							is_published: lesson.is_published,
							is_draft: lesson.is_draft,
						},
					}))

					// Обновляем массивы из initialMaterials - ТЕСТЫ
					const sortedInitialTests = this.sortMaterialsData(
						newValue.tests || [],
						['test_id', 'testId'],
						this.tests,
					)
					this.tests = sortedInitialTests.map(test => {
						// Парсим scheduled_publish_at (может быть ISO или "YYYY-MM-DD HH:MM")
						const scheduledAt = test.scheduled_publish_at || test.fullData?.scheduled_publish_at || null
						let publishDate = ''
						let publishTime = ''
						if (scheduledAt) {
							if (typeof scheduledAt === 'string' && scheduledAt.includes('T')) {
								// ISO формат из БД "2025-12-24T11:30:00.000Z"
								const date = new Date(scheduledAt)
								const year = date.getFullYear()
								const month = String(date.getMonth() + 1).padStart(2, '0')
								const day = String(date.getDate()).padStart(2, '0')
								const hours = String(date.getHours()).padStart(2, '0')
								const minutes = String(date.getMinutes()).padStart(2, '0')
								publishDate = `${year}-${month}-${day}`
								publishTime = `${hours}:${minutes}`
							} else if (typeof scheduledAt === 'string' && scheduledAt.includes(' ')) {
								// Формат "2025-12-23 12:30"
								const parts = scheduledAt.split(' ')
								publishDate = parts[0] || ''
								publishTime = parts[1] ? parts[1].slice(0, 5) : ''
							} else {
								const date = scheduledAt instanceof Date ? scheduledAt : new Date(scheduledAt)
								if (!Number.isNaN(date.getTime())) {
									const year = date.getFullYear()
									const month = String(date.getMonth() + 1).padStart(2, '0')
									const day = String(date.getDate()).padStart(2, '0')
									const hours = String(date.getHours()).padStart(2, '0')
									const minutes = String(date.getMinutes()).padStart(2, '0')
									publishDate = `${year}-${month}-${day}`
									publishTime = `${hours}:${minutes}`
								}
							}
						}

						return {
							...test,
							//  ВАЖНО: копируем scheduled_publish_at на верхний уровень для transformMaterialsForDisplay
							scheduled_publish_at: scheduledAt,
							is_hidden: test.is_hidden ?? test.fullData?.is_hidden ?? !!scheduledAt,
							fullData: {
								...(test.fullData || {}),
								id: test.id,
								title: test.title,
								description: test.description || '',
								duration:
									test.fullData?.duration ??
									test.duration ??
									test.duration_minutes ??
									0,
								passingScore:
									test.fullData?.passingScore ??
									test.passingScore ??
									test.passing_score ??
									70,
								attempts:
									test.fullData?.attempts ??
									test.attempts ??
									test.max_attempts ??
									1,
								questionsCount:
									test.fullData?.questionsCount ??
									test.questionsCount ??
									test.questions_count ??
									test.questions?.length ??
									0,
								questions: test.fullData?.questions ?? test.questions ?? [],
								randomOrder:
									test.fullData?.randomOrder ??
									test.randomOrder ??
									test.random_order ??
									false,
								showCorrectAnswers:
									test.fullData?.showCorrectAnswers ??
									test.showCorrectAnswers ??
									test.show_correct_answers ??
									true,
								allowReview:
									test.fullData?.allowReview ??
									test.allowReview ??
									test.allow_review ??
									true,
								penalizeWrong:
									test.fullData?.penalizeWrong ??
									test.penalizeWrong ??
									test.penalize_wrong ??
									false,
								penaltyPercentage:
									test.fullData?.penaltyPercentage ??
									test.penaltyPercentage ??
									test.penalty_percentage ??
									50,
								disableCopying:
									test.fullData?.disableCopying ??
									test.disableCopying ??
									test.disable_copying ??
									false,
								limitTimePerQuestion:
									test.fullData?.limitTimePerQuestion ??
									test.limitTimePerQuestion ??
									test.limit_time_per_question ??
									false,
								timePerQuestion:
									test.fullData?.timePerQuestion ??
									test.timePerQuestion ??
									test.time_per_question ??
									0,
								selectRandomQuestions:
									test.fullData?.selectRandomQuestions ??
									test.selectRandomQuestions ??
									test.select_random_questions ??
									false,
								questionsToShow:
									test.fullData?.questionsToShow ??
									test.questionsToShow ??
									test.questions_to_show ??
									0,
								//  Поля автопубликации
								scheduled_publish_at: scheduledAt,
								isScheduledPublish: !!scheduledAt,
								autoPublish: !scheduledAt,
								publishDate: test.fullData?.publishDate ?? publishDate,
								publishTime: test.fullData?.publishTime ?? publishTime,
								is_hidden: test.is_hidden ?? test.fullData?.is_hidden ?? !!scheduledAt,
								isDraft:
									test.fullData?.isDraft ??
									test.is_draft ??
									test.isDraft ??
									false,
								is_draft:
									test.fullData?.is_draft ??
									test.is_draft ??
									test.isDraft ??
									false,
							},
						}
					})

					// Обновляем массивы из initialMaterials - ПРАКТИЧЕСКИЕ ЗАДАНИЯ
					const sortedInitialPractices = this.sortMaterialsData(
						newValue.practice || [],
						['task_id', 'taskId', 'practical_id', 'practicalId'],
						this.practices,
					)
					this.practices = sortedInitialPractices.map(practice => ({
						...practice,
						fullData: {
							...(practice.fullData || {}),
							id: practice.id,
							title: practice.title,
							content:
								practice.content ||
								practice.fullData?.content ||
								practice.task_description ||
								practice.fullData?.goal ||
								practice.fullData?.description ||
								'',
							description:
								practice.content ||
								practice.fullData?.content ||
								practice.task_description ||
								practice.fullData?.goal ||
								practice.fullData?.description ||
								'',
							instructions:
								practice.instructions || practice.fullData?.instructions || '',
							dueDate: practice.due_date || practice.fullData?.dueDate || null,
							dueTime: practice.due_time || practice.fullData?.dueTime || null,
							maxScore:
								practice.max_score || practice.fullData?.maxScore || 100,
							materials:
								practice.materials || practice.fullData?.materials || [],
							questions: practice.fullData?.questions || [],
							miniTest:
								practice.fullData?.miniTest || practice.mini_test || null,
							mini_test:
								practice.fullData?.mini_test || practice.mini_test || null,
							includeMiniTest:
								practice.fullData?.includeMiniTest ||
								!!practice.mini_test ||
								false,
						},
					}))

					// Обновляем массивы из initialMaterials - ДОМАШНИЕ ЗАДАНИЯ
					const sortedInitialHomeworks = this.sortMaterialsData(
						newValue.homework || [],
						['homework_id', 'homeworkId', 'task_id', 'taskId'],
						this.homeworks,
					)
					this.homeworks = sortedInitialHomeworks.map(homework => {
						let parsedRequirements = homework.requirements || []
						if (typeof parsedRequirements === 'string') {
							try {
								parsedRequirements = JSON.parse(parsedRequirements)
							} catch {
								parsedRequirements = parsedRequirements
									? [parsedRequirements]
									: []
							}
						}

						let parsedMaterials = homework.materials || []
						if (typeof parsedMaterials === 'string') {
							try {
								parsedMaterials = JSON.parse(parsedMaterials)
							} catch {
								parsedMaterials = []
							}
						}

						let formattedDueDate = homework.due_date || null
						if (formattedDueDate instanceof Date) {
							formattedDueDate = formattedDueDate.toISOString().split('T')[0]
						} else if (typeof formattedDueDate === 'string' && formattedDueDate.includes('T')) {
							formattedDueDate = formattedDueDate.split('T')[0]
						}

						let formattedDueTime = homework.due_time || null
						if (formattedDueTime && formattedDueTime.length > 5) {
							formattedDueTime = formattedDueTime.substring(0, 5)
						}

						return {
							...homework,
							fullData: {
								...(homework.fullData || {}),
								id: homework.id,
								title: homework.title,
								description: homework.description || '',
								requirements: parsedRequirements,
								dueDate: formattedDueDate,
								dueTime: formattedDueTime,
								maxScore: homework.max_score ?? homework.maxScore ?? null,
								allowLateSubmission:
									homework.allow_late_submission ??
									homework.allowLateSubmission ??
									homework.fullData?.allowLateSubmission ??
									null,
								materials: parsedMaterials,
								isDraft: homework.is_draft ?? homework.isDraft ?? false,
								is_draft: homework.is_draft ?? homework.isDraft ?? false,
							},
						}
					})

					//  ДОБАВЛЕНО: Восстанавливаем материалы с локальными изменениями - УРОКИ
					localChangedLessons.forEach(localLesson => {
						const index = this.lessons.findIndex(l => l.id === localLesson.id)
						if (index !== -1) {
							this.lessons[index] = localLesson
							console.log(
								` Восстановлен урок с локальными изменениями: ${localLesson.id}`
							)
						} else {
							this.lessons.push(localLesson)
							console.log(
								` Добавлен новый урок с локальными изменениями: ${localLesson.id}`
							)
						}
					})

					//  ДОБАВЛЕНО: Восстанавливаем материалы с локальными изменениями - ТЕСТЫ
					localChangedTests.forEach(localTest => {
						const index = this.tests.findIndex(t => t.id === localTest.id)
						if (index !== -1) {
							this.tests[index] = localTest
							console.log(
								` Восстановлен тест с локальными изменениями: ${localTest.id}`
							)
						} else {
							this.tests.push(localTest)
							console.log(
								` Добавлен новый тест с локальными изменениями: ${localTest.id}`
							)
						}
					})

					//  ДОБАВЛЕНО: Восстанавливаем материалы с локальными изменениями - ПРАКТИЧЕСКИЕ ЗАДАНИЯ
					localChangedPractices.forEach(localPractice => {
						const index = this.practices.findIndex(
							p => p.id === localPractice.id
						)
						if (index !== -1) {
							this.practices[index] = localPractice
							console.log(
								` Восстановлено практическое задание с локальными изменениями: ${localPractice.id}`
							)
						} else {
							this.practices.push(localPractice)
							console.log(
								` Добавлено новое практическое задание с локальными изменениями: ${localPractice.id}`
							)
						}
					})

					//  ДОБАВЛЕНО: Восстанавливаем материалы с локальными изменениями - ДОМАШНИЕ ЗАДАНИЯ
					localChangedHomeworks.forEach(localHomework => {
						const index = this.homeworks.findIndex(
							h => h.id === localHomework.id
						)
						if (index !== -1) {
							this.homeworks[index] = localHomework
							console.log(
								` Восстановлено домашнее задание с локальными изменениями: ${localHomework.id}`
							)
						} else {
							this.homeworks.push(localHomework)
							console.log(
								` Добавлено новое домашнее задание с локальными изменениями: ${localHomework.id}`
							)
						}
					})

					console.log(
						' Локальные массивы обновлены с сохранением локальных изменений:',
						{
							lessons: this.lessons.length,
							tests: this.tests.length,
							practices: this.practices.length,
							homeworks: this.homeworks.length,
						}
					)
				}
			},
			deep: true,
			immediate: false,
		},
	},

	methods: {
		isTempModuleId() {
			if (this.moduleId === null || this.moduleId === undefined || this.moduleId === '') {
				return true
			}

			const moduleIdStr = String(this.moduleId)
			if (moduleIdStr.startsWith('temp_')) {
				return true
			}

			const moduleIdNumber = Number(this.moduleId)
			if (!Number.isFinite(moduleIdNumber)) {
				return true
			}

			if (!Number.isInteger(moduleIdNumber) || moduleIdNumber <= 0) {
				return true
			}

			return moduleIdNumber >= 1000000000000
		},

		isValidDateYYYYMMDD(value) {
			if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
				return false
			}

			const [yearStr, monthStr, dayStr] = value.split('-')
			const year = Number(yearStr)
			const month = Number(monthStr)
			const day = Number(dayStr)

			if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
				return false
			}
			if (month < 1 || month > 12) return false
			if (day < 1 || day > 31) return false

			// Валидация через UTC, чтобы избежать сдвигов таймзоны
			const date = new Date(Date.UTC(year, month - 1, day))
			return (
				date.getUTCFullYear() === year &&
				date.getUTCMonth() === month - 1 &&
				date.getUTCDate() === day
			)
		},

		isValidTimeHHMM(value) {
			if (typeof value !== 'string' || !/^\d{2}:\d{2}$/.test(value)) {
				return false
			}

			const [hoursStr, minutesStr] = value.split(':')
			const hours = Number(hoursStr)
			const minutes = Number(minutesStr)

			if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
				return false
			}

			return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59
		},

		buildScheduledDateTime(publishDate, publishTime) {
			if (!this.isValidDateYYYYMMDD(publishDate) || !this.isValidTimeHHMM(publishTime)) {
				return null
			}
			return `${publishDate} ${publishTime}`
		},

		parseScheduledAtToDate(scheduledAt) {
			if (!scheduledAt) return null

			if (scheduledAt instanceof Date) {
				return Number.isNaN(scheduledAt.getTime()) ? null : scheduledAt
			}

			if (typeof scheduledAt === 'number') {
				const date = new Date(scheduledAt)
				return Number.isNaN(date.getTime()) ? null : date
			}

			if (typeof scheduledAt !== 'string') {
				const date = new Date(scheduledAt)
				return Number.isNaN(date.getTime()) ? null : date
			}

			let normalized = scheduledAt
			if (normalized.includes(' ') && !normalized.includes('T')) {
				normalized = normalized.replace(' ', 'T')
			}

			const date = new Date(normalized)
			return Number.isNaN(date.getTime()) ? null : date
		},
		/**
		 *  Скролл к карточке материала после закрытия формы
		 */
		scrollToMaterial(materialId, materialType) {
			if (!materialId) {
				console.log('⚠️ Нет ID для скролла')
				return
			}

			this.$nextTick(() => {
				setTimeout(() => {
					// Ищем карточку материала по data-атрибуту
					const selectors = [
						`[data-material-id="${materialId}"]`,
						`[data-${materialType}-id="${materialId}"]`,
						`[data-item-id="${materialId}"]`,
					]

					let element = null
					for (const selector of selectors) {
						element = document.querySelector(selector)
						if (element) break
					}

					if (element) {
						console.log(`📜 Скролл к материалу ${materialType}-${materialId}`)
						element.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						})

						// Подсветка элемента
						element.classList.add('mts-highlight-animation')
						setTimeout(() => {
							element.classList.remove('mts-highlight-animation')
						}, 2000)
					} else {
						// Если элемент не найден, скроллим к секции материалов
						console.log(
							`⚠️ Элемент ${materialType}-${materialId} не найден, скроллим к секции`
						)
						const materialsSection = document.querySelector('.mts-materials')
						if (materialsSection) {
							materialsSection.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
							})
						}
					}
				}, 150) // Небольшая задержка для закрытия формы
			})
		},

		//  Преобразование материалов для отображения
		getSortIdKey(item, idKeys = []) {
			const candidates = [item?.id, item?._id, ...idKeys.map(key => item?.[key])]
			const found = candidates.find(
				value => value !== null && value !== undefined,
			)
			if (found === null || found === undefined) return null
			return String(found)
		},

		sortMaterialsData(items, idKeys = [], previousItems = []) {
			if (!Array.isArray(items)) return []
			const previousOrderMap = new Map()
			if (Array.isArray(previousItems)) {
				previousItems.forEach((item, index) => {
					const key = this.getSortIdKey(item, idKeys)
					if (key !== null) {
						previousOrderMap.set(key, index)
					}
				})
			}
			return items
				.map((item, index) => ({ item, index }))
				.sort((a, b) => {
					const prevIndexA = previousOrderMap.get(
						this.getSortIdKey(a.item, idKeys),
					)
					const prevIndexB = previousOrderMap.get(
						this.getSortIdKey(b.item, idKeys),
					)
					const hasPrevA = prevIndexA !== undefined
					const hasPrevB = prevIndexB !== undefined
					if (hasPrevA || hasPrevB) {
						if (hasPrevA && hasPrevB && prevIndexA !== prevIndexB) {
							return prevIndexA - prevIndexB
						}
						if (hasPrevA && !hasPrevB) return -1
						if (!hasPrevA && hasPrevB) return 1
					}
					const getOrderValue = item => {
						const value =
							item?.order ??
							item?.order_number ??
							item?.position ??
							item?.sort_order ??
							item?.sortOrder ??
							null
						const numeric = Number(value)
						return Number.isFinite(numeric) ? numeric : null
					}
					const getCreatedAtValue = item => {
						const value =
							item?.created_at ??
							item?.createdAt ??
							item?.created ??
							item?.createdOn ??
							null
						if (value === null || value === undefined) return null
						const parsed = value instanceof Date ? value : new Date(value)
						if (!Number.isNaN(parsed.getTime())) return parsed.getTime()
						const numeric = Number(value)
						return Number.isFinite(numeric) ? numeric : null
					}
					const getIdValue = item => {
						const found = this.getSortIdKey(item, idKeys)
						const numeric = Number(found)
						return Number.isFinite(numeric) ? numeric : null
					}
					const orderA = getOrderValue(a.item)
					const orderB = getOrderValue(b.item)
					if (orderA !== null && orderB !== null && orderA !== orderB) {
						return orderA - orderB
					}
					const createdA = getCreatedAtValue(a.item)
					const createdB = getCreatedAtValue(b.item)
					if (createdA !== null && createdB !== null && createdA !== createdB) {
						return createdA - createdB
					}
					const idA = getIdValue(a.item)
					const idB = getIdValue(b.item)
					if (idA !== null && idB !== null && idA !== idB) {
						return idA - idB
					}
					return a.index - b.index
				})
				.map(entry => entry.item)
		},

		transformMaterialsForDisplay(materials, type) {
			console.log(`[transformMaterialsForDisplay] Тип: ${type}, Материалов: ${materials?.length || 0}`)
			
			if (!materials || materials.length === 0) {
				console.log(`[transformMaterialsForDisplay] Нет материалов для отображения`)
				return []
			}

			console.log(`[transformMaterialsForDisplay] Исходные материалы:`, materials)

			// Префиксы для нумерации по типу
			const prefixMap = {
				lessons: 'Урок',
				tests: 'Тест',
				practice: 'Практика',
				homework: 'ДЗ',
			}
			const prefix = prefixMap[type] || ''

			return materials.map((material, index) => {
				const isDraft =
					material.is_draft ??
					material.isDraft ??
					material.fullData?.is_draft ??
					material.fullData?.isDraft ??
					false

				const inferredHidden =
					(material.is_draft !== true &&
						material.fullData?.is_draft !== true &&
						(material.is_published === false ||
							material.fullData?.is_published === false))

				const isHidden =
					material.is_hidden ??
					material.isHidden ??
					material.fullData?.is_hidden ??
					material.fullData?.isHidden ??
					inferredHidden

				// Форматируем номер с ведущим нулём (01, 02, ...)
				const num = String(index + 1).padStart(2, '0')
				const originalTitle = material.title || 'Без названия'
				// Формируем название: "Тест 01: Название теста"
				const formattedTitle = `${prefix} ${num}: ${originalTitle}`

				const baseData = {
					id: material.id,
					title: formattedTitle,
					isDraft,
					isHidden,
				}

				if (type === 'lessons') {
					// Получаем scheduled_publish_at из разных источников
					const scheduledAt =
						material.scheduled_publish_at ||
						material.fullData?.scheduled_publish_at

					console.log(`[LESSON ${material.id}] Обработка урока:`, {
						title: originalTitle,
						scheduledAt,
						isDraft,
						isHidden,
						material
					})

					// Проверяем время автопубликации
					let shouldBeHidden = isHidden
					let scheduledPublishAt = scheduledAt

					if (scheduledAt) {
						// Парсим дату
						const scheduledDate = this.parseScheduledAtToDate(scheduledAt)

						const now = new Date()
						console.log(`[LESSON ${material.id}] Проверка времени:`, {
							scheduledDate: scheduledDate?.toISOString(),
							now: now.toISOString(),
							isPast: scheduledDate && scheduledDate <= now
						})

						// Если время НЕ наступило - урок скрыт
						if (scheduledDate && scheduledDate > now) {
							shouldBeHidden = true
							console.log(`[LESSON ${material.id}] Урок будет скрыт до ${scheduledDate.toISOString()}`)
						} else {
							shouldBeHidden = false
							console.log(`[LESSON ${material.id}] Время публикации прошло, урок виден`)
						}
					}

					const result = {
						...baseData,
						isHidden: shouldBeHidden,
						scheduledPublishAt,
						lessonType:
							material.type ||
							material.lesson_type ||
							material.fullData?.type ||
							'video',
						duration:
							material.duration ||
							material.duration_minutes ||
							material.fullData?.duration ||
							0,
						autoPublish: scheduledAt ? 'scheduled' : 'now',
					}

					console.log(`[LESSON ${material.id}] Результат:`, result)
					return result
				} else if (type === 'tests') {
					// Форматируем дату автопубликации для отображения на карточке
					let autoPublishLabel = ''
					let shouldBeHidden = isHidden
					const scheduledAt =
						material.scheduled_publish_at ||
						material.fullData?.scheduled_publish_at

					if (scheduledAt) {
						// Парсим дату для проверки - прошло ли время автопубликации
						const scheduledDate = this.parseScheduledAtToDate(scheduledAt)

						const now = new Date()

						// Если время автопубликации ещё НЕ наступило - показываем дату и тест скрыт
						if (scheduledDate && scheduledDate > now) {
							const day = String(scheduledDate.getDate()).padStart(2, '0')
							const month = String(scheduledDate.getMonth() + 1).padStart(2, '0')
							const year = String(scheduledDate.getFullYear()).slice(-2)
							const hours = String(scheduledDate.getHours()).padStart(2, '0')
							const minutes = String(scheduledDate.getMinutes()).padStart(2, '0')
							autoPublishLabel = `${day}.${month}.${year} ${hours}:${minutes}`
							shouldBeHidden = true
						} else {
							// Время прошло - тест должен быть видимым, автопубликация не показывается
							autoPublishLabel = ''
							shouldBeHidden = false
						}
					}

					return {
						...baseData,
						//  Переопределяем isHidden с учётом времени автопубликации
						isHidden: shouldBeHidden,
						// Форматированная дата автопубликации для карточки
						autoPublish: autoPublishLabel,
						//  Передаём scheduledPublishAt для отображения на карточке
						scheduledPublishAt: scheduledAt,
						// ИСПРАВЛЕНО: сначала проверяем fullData, потом material
						questionsCount:
							material.fullData?.questionsCount ??
							material.fullData?.questions?.length ??
							material.questionsCount ??
							material.questions_count ??
							0,
						passingScore:
							material.fullData?.passingScore ??
							material.passingScore ??
							material.passing_score ??
							70,
						attempts:
							material.fullData?.attempts ??
							material.attempts ??
							material.max_attempts ??
							1,
						duration:
							material.fullData?.duration ??
							material.duration ??
							material.duration_minutes ??
							0,
					}
				} else if (type === 'homework' || type === 'practice') {
					return {
						...baseData,
						dueDate: material.fullData?.dueDate || material.due_date || null,
						dueTime: material.fullData?.dueTime || material.due_time || null,
						duration:
							material.fullData?.duration || material.duration_minutes || 0,
					}
				}

				return baseData
			})
		},

		clearLocalChangesFlag(materialId, materialType) {
			const materialsMap = {
				lessons: this.lessons,
				practice: this.practices,
				homework: this.homeworks,
				tests: this.tests,
			}

			const materials = materialsMap[materialType]
			if (!materials) return

			const material = materials.find(m => m.id === materialId)
			if (material) {
				material.hasLocalChanges = false
				if (material.fullData) {
					material.fullData.hasLocalChanges = false
				}
				console.log(
					` Сброшен флаг hasLocalChanges для материала ${materialId} типа ${materialType}`
				)
			}
		},

		//  НОВЫЙ МЕТОД: Сброс всех флагов после полного сохранения модуля
		clearAllLocalChangesFlags() {
			const allMaterials = [
				...this.lessons,
				...this.tests,
				...this.practices,
				...this.homeworks,
			]

			allMaterials.forEach(material => {
				material.hasLocalChanges = false
				if (material.fullData) {
					material.fullData.hasLocalChanges = false
				}
			})

			console.log(' Сброшены все флаги hasLocalChanges для всех материалов')
		},

		handleTabChange(tabId) {
			this.activeTab = tabId
			this.$emit('tab-changed', tabId)
		},

		saveActiveTab(tabId) {
			try {
				localStorage.setItem('moduleTestsSection_activeTab', tabId)
			} catch (error) {
				console.warn('Не удалось сохранить активную вкладку:', error)
			}
		},

		restoreActiveTab() {
			try {
				const savedTab = localStorage.getItem('moduleTestsSection_activeTab')
				if (savedTab && this.visibleTabs.some(tab => tab.id === savedTab)) {
					this.activeTab = savedTab
					return
				}
			} catch (error) {
				console.warn('Не удалось восстановить активную вкладку:', error)
			}

			if (!this.visibleTabs.some(tab => tab.id === this.activeTab)) {
				this.activeTab = this.visibleTabs[0]?.id || 'lessons'
			}
		},

		handleCreateItem() {
			console.log(`Создание элемента для вкладки: ${this.activeTab}`)

			//  Сбрасываем editingMaterial и editingMaterialType для создания нового элемента
			this.editingMaterial = null
			this.editingMaterialIndex = null
			this.editingMaterialType = null

			//  ВАЖНО: Используем $nextTick чтобы editingMaterial = null успел обновиться
			// ПЕРЕД тем как компонент будет перемонтирован с новым key
			this.$nextTick(() => {
				switch (this.activeTab) {
					case 'tests':
						//  Увеличиваем testFormKey для перемонтирования компонента
						this.testFormKey++
						this.showTestCreation = true
						// Сохраняем состояние в localStorage
						this.saveFormState('test', true, null)
						break
					case 'lessons':
						//  Увеличиваем lessonFormKey для перемонтирования компонента
						this.lessonFormKey++
						this.showLessonCreation = true
						// Сохраняем состояние в localStorage
						this.saveFormState('lesson', true, null)
						break
					case 'practice':
						//  Увеличиваем practiceFormKey для перемонтирования компонента
						this.practiceFormKey++
						this.showPracticeCreation = true
						// Сохраняем состояние в localStorage
						this.saveFormState('practice', true, null)
						break
					case 'homework':
						//  Увеличиваем homeworkFormKey для перемонтирования компонента
						this.homeworkFormKey++
						this.showHomeworkCreation = true
						// Сохраняем состояние в localStorage
						this.saveFormState('homework', true, null)
						break
				}
			})

			//  Автоскролл к форме создания материала
			this.$nextTick(() => {
				setTimeout(() => {
					const classMap = {
						tests: '.mts-test-creation',
						lessons: '.mts-lesson-creation',
						practice: '.mts-practice-creation',
						homework: '.mts-homework-creation',
					}

					const selector = classMap[this.activeTab]
					if (selector) {
						const element = document.querySelector(selector)
						if (element) {
							element.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
							})
							console.log(` Автоскролл к форме: ${selector}`)
						}
					}
				}, 100)
			})

			const eventMap = {
				lessons: 'create-lesson',
				practice: 'create-practice',
				homework: 'create-homework',
				tests: 'create-test',
			}

			const eventName = eventMap[this.activeTab] || 'create-item'
			this.$emit(eventName, this.activeTab)
		},

		//  УЛУЧШЕННЫЙ МЕТОД СОХРАНЕНИЯ ТЕСТА
		async handleSaveTest(testData) {
			console.log('🔍 Сохранение теста:', testData)
			console.log('📍 Режим работы:', this.isLocalMode ? 'ЛОКАЛЬНЫЙ' : 'БД')

			if (this.isTempModuleId()) {
				this.showNotification('Ошибка: Сначала сохраните модуль, затем добавляйте элементы', 'error')
				return
			}

			//  Проверяем, редактируем ли мы существующий тест или создаем новый
			const isEditMode = testData.id !== undefined && testData.id !== null

			//  Создаем/обновляем тест
			// Если по расписанию - тест скрыт (не черновик!), показываем дату автопубликации
			const isScheduled = testData.autoPublish === false
			const scheduledDateTime = isScheduled
				? this.buildScheduledDateTime(testData.publishDate, testData.publishTime)
				: null

			if (isScheduled && !scheduledDateTime) {
				this.showNotification(
					'Ошибка: укажите корректные дату и время автопубликации (ГГГГ-ММ-ДД и ЧЧ:ММ)',
					'error'
				)
				return
			}

			const testElement = {
				id: isEditMode ? testData.id : Date.now(),
				title: testData.title || 'Новый тест',
				description: testData.description || '',
				hasLocalChanges: true,
				//  Дублируем поля в обоих форматах для совместимости
				duration_minutes: testData.duration ?? 0,
				duration: testData.duration ?? 0, // для MaterialCard
				passing_score: testData.passingScore ?? 70,
				passingScore: testData.passingScore ?? 70, // для MaterialCard
				max_attempts: testData.attempts ?? 1,
				attempts: testData.attempts ?? 1, // для MaterialCard
				questions: testData.questions || [],
				questions_count: testData.questions?.length || 0,
				questionsCount: testData.questions?.length || 0, // для MaterialCard
				is_published: !isScheduled, // Опубликован сразу если НЕ по расписанию
				is_draft: false, // Никогда не черновик при нажатии "Создать тест"
				is_hidden: isScheduled, // Скрыт если по расписанию
				scheduled_publish_at: scheduledDateTime,
				//  Добавляем fullData для сохранения в БД через saveLocalMaterialsToDB
				//  ИСПРАВЛЕНО: Сохраняем ВСЕ поля для корректного редактирования
				fullData: {
					id: isEditMode ? testData.id : Date.now(),
					title: testData.title || 'Новый тест',
					description: testData.description || '',
					module: testData.module || this.moduleId,
					category: testData.category || '',
					//  Используем ?? вместо || чтобы 0 не превращался в дефолт
					duration: testData.duration ?? 0,
					passingScore: testData.passingScore ?? 70,
					attempts: testData.attempts ?? 1,
					pointsPerQuestion: testData.pointsPerQuestion ?? 1,
					// Дополнительные настройки
					randomOrder: testData.randomOrder ?? false,
					showCorrectAnswers: testData.showCorrectAnswers ?? true,
					allowReview: testData.allowReview ?? true,
					showProgressBar: testData.showProgressBar ?? true,
					penalizeWrong: testData.penalizeWrong ?? false,
					penaltyPercentage: testData.penaltyPercentage ?? 50,
					disableCopying: testData.disableCopying ?? false,
					limitTimePerQuestion: testData.limitTimePerQuestion ?? false,
					timePerQuestion: testData.timePerQuestion ?? 0,
					selectRandomQuestions: testData.selectRandomQuestions ?? false,
					questionsToShow: testData.questionsToShow ?? 0,
					questionDistributionMode:
						testData.questionDistributionMode ?? 'random',
					questionTypeDistribution:
						testData.questionTypeDistribution ?? {
							single: 0,
							multiple: 0,
							text: 0,
						},
					// Публикация
					isScheduledPublish: isScheduled,
					autoPublish: !isScheduled,
					publishDate: testData.publishDate || null,
					publishTime: testData.publishTime || null,
					scheduled_publish_at: scheduledDateTime,
					questions: testData.questions || [],
					questionsCount: testData.questions?.length || 0,
					totalPoints: testData.totalPoints ?? 0,
					is_published: !isScheduled,
					is_draft: false, // Никогда не черновик
					is_hidden: isScheduled, // Скрыт если по расписанию
					hasLocalChanges: true, //  Флаг локальных изменений
				},
			}

			if (isEditMode) {
				//  ВСЕГДА обновляем тест в локальном массиве (для сохранения локальных изменений)
				const index = this.tests.findIndex(t => t.id === testElement.id)
				if (index !== -1) {
					this.tests[index] = testElement
					console.log(' Тест обновлен в локальном массиве:', testElement)
				}

				this.$emit('element-updated', {
					type: 'tests',
					element: testElement,
				})
				this.showNotification('Тест обновлен локально', 'success')
			} else {
				//  ВСЕГДА добавляем тест в локальный массив
				this.tests.push(testElement)
				console.log(' Тест добавлен в локальный массив:', testElement)
				console.log('📊 Всего тестов в локальном массиве:', this.tests.length)

				this.$emit('element-created', {
					type: 'tests',
					element: testElement,
				})
				this.showNotification('Тест создан локально', 'success')
			}

			//  Закрываем форму после создания/обновления
			this.showTestCreation = false
			this.editingMaterial = null
			this.editingMaterialType = null
			this.testFormKey++

			//  Автоскролл к созданному тесту
			this.$nextTick(() => {
				setTimeout(() => {
					const materialsSection = document.querySelector('.mts-materials')
					if (materialsSection) {
						materialsSection.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						})
					}
				}, 100)
			})
		},

		async handleSaveLesson(lessonData) {
			console.log('🔍 Сохранение урока:', lessonData)
			console.log('📍 Режим работы:', this.isLocalMode ? 'ЛОКАЛЬНЫЙ' : 'БД')

			if (this.isTempModuleId()) {
				this.showNotification('Ошибка: Сначала сохраните модуль, затем добавляйте элементы', 'error')
				return
			}

			//  Проверяем, редактируем ли мы существующий урок или создаем новый
			const isEditMode = lessonData.id !== undefined && lessonData.id !== null

			const publishTypeValue = lessonData.publishType || 'immediate'
			let scheduledDateTime = null
			if (publishTypeValue === 'scheduled') {
				scheduledDateTime = this.buildScheduledDateTime(
					lessonData.publishDate,
					lessonData.publishTime
				)
				if (!scheduledDateTime) {
					this.showNotification(
						'Укажите корректные дату и время автопубликации',
						'error'
					)
					return
				}
			}

			const enableTocValue = lessonData.enableToc ?? lessonData.enable_toc ?? false
			const tocModeValue = lessonData.tocMode ?? lessonData.toc_mode ?? 'auto'
			const disableMainMaterialsDownloadValue =
				lessonData.disableMainMaterialsDownload ??
				lessonData.disable_main_materials_download ??
				false
			const disableAdditionalMaterialsDownloadValue =
				lessonData.disableAdditionalMaterialsDownload ??
				lessonData.disable_additional_materials_download ??
				false

			const lessonElement = {
				id: isEditMode ? lessonData.id : Date.now(),
				title: lessonData.title || 'Новый урок',
				description: lessonData.description || '',
				hasLocalChanges: true,
				content: lessonData.content || lessonData.articleContent || '',
				//  video_url всегда строка (внешний URL или путь из БД)
				video_url: lessonData.videoUrl || '',
				cover_image_url:
					lessonData.videoCoverUrl ||
					lessonData.cover_image_url ||
					lessonData.cover_image ||
					null,
				duration_minutes: lessonData.duration || 30,
				lesson_type: lessonData.type || 'video',
				is_published: publishTypeValue !== 'scheduled',
				is_draft: false,
				//  ИСПРАВЛЕНО: is_hidden зависит от scheduled_publish_at
				is_hidden: publishTypeValue === 'scheduled',
				scheduled_publish_at:
					publishTypeValue === 'scheduled' ? scheduledDateTime
						: null,
				enable_toc: enableTocValue,
				enableToc: enableTocValue,
				toc_mode: tocModeValue,
				tocMode: tocModeValue,
				disable_main_materials_download: disableMainMaterialsDownloadValue,
				disableMainMaterialsDownload: disableMainMaterialsDownloadValue,
				disable_additional_materials_download:
					disableAdditionalMaterialsDownloadValue,
				disableAdditionalMaterialsDownload:
					disableAdditionalMaterialsDownloadValue,
				materials: lessonData.materials || [],
				//  videoFile используем только для File-объекта (если он есть)
				videoFile:
					lessonData.videoFile instanceof File ? lessonData.videoFile : null,
				docFile: lessonData.docFile,
				deletedMaterials: lessonData.deletedMaterials || [], //  Передаем список удаленных файлов
				//  Добавляем fullData для сохранения в БД через saveLocalMaterialsToDB
				//  ИСПРАВЛЕНО: Сохраняем ВСЕ поля для корректного редактирования
				fullData: {
					id: isEditMode ? lessonData.id : Date.now(), //  Добавлен id
					title: lessonData.title || 'Новый урок',
					module: lessonData.module || this.moduleId, //  Добавлен module
					publishType: publishTypeValue, //  Добавлен publishType
					publishDate: lessonData.publishDate || '', //  Добавлен publishDate
					publishTime: lessonData.publishTime || '', //  Добавлен publishTime
					type: lessonData.type || 'video',
					description: lessonData.description || '',
					//  videoFile в fullData также только File | null
					videoFile:
						lessonData.videoFile instanceof File ? lessonData.videoFile : null,
					//  отдельное поле для ссылки (внешний URL или путь из БД)
					videoUrl: lessonData.videoUrl || '',
					videoCoverFile:
						lessonData.videoCoverFile instanceof File
							? lessonData.videoCoverFile
							: null,
					videoCoverUrl:
						lessonData.videoCoverUrl ||
						lessonData.cover_image_url ||
						lessonData.cover_image ||
						'',
					cover_image_url:
						lessonData.videoCoverUrl ||
						lessonData.cover_image_url ||
						lessonData.cover_image ||
						null,
					docFile: lessonData.docFile,
					deletedDocFile: lessonData.deletedDocFile || false, //  Флаг удаления документа
					articleContent: lessonData.articleContent || lessonData.content || '', //  Переименовано в articleContent
					content: lessonData.content || lessonData.articleContent || '',
					duration: lessonData.duration || 30,
					materials: lessonData.materials || [],
					deletedMaterials: lessonData.deletedMaterials || [], //  Добавлен deletedMaterials
					is_published: publishTypeValue !== 'scheduled',
					is_draft: false,
					//  ИСПРАВЛЕНО: is_hidden зависит от scheduled_publish_at
					is_hidden: publishTypeValue === 'scheduled',
					scheduled_publish_at:
					publishTypeValue === 'scheduled' ? scheduledDateTime
						: null,
					enable_toc: enableTocValue,
					enableToc: enableTocValue,
					toc_mode: tocModeValue,
					tocMode: tocModeValue,
					disable_main_materials_download: disableMainMaterialsDownloadValue,
					disableMainMaterialsDownload: disableMainMaterialsDownloadValue,
					disable_additional_materials_download:
						disableAdditionalMaterialsDownloadValue,
					disableAdditionalMaterialsDownload:
						disableAdditionalMaterialsDownloadValue,
					hasLocalChanges: true, //  Флаг локальных изменений
				},
			}

			console.log('[handleSaveLesson] Сформированный lessonElement:', {
				id: lessonElement.id,
				title: lessonElement.title,
				is_hidden: lessonElement.is_hidden,
				scheduled_publish_at: lessonElement.scheduled_publish_at,
				publishType: publishTypeValue
			})

			console.log(
				'🗑️ Удаленные файлы в handleSaveLesson:',
				lessonData.deletedMaterials
			)

			if (isEditMode) {
				//  ВСЕГДА обновляем урок в локальном массиве (для сохранения локальных изменений)
				const index = this.lessons.findIndex(l => l.id === lessonElement.id)
				if (index !== -1) {
					console.log(`[handleSaveLesson] Обновление урока ${lessonElement.id}:`, {
						old: this.lessons[index],
						new: lessonElement
					})
					this.lessons[index] = lessonElement
					console.log(' Урок обновлен в локальном массиве:', lessonElement)
				} else {
					console.warn(`[handleSaveLesson] Урок ${lessonElement.id} не найден в массиве!`)
				}

				this.$emit('element-updated', {
					type: 'lessons',
					element: lessonElement,
				})
				this.showNotification('Урок обновлен локально', 'success')
			} else {
				//  ВСЕГДА добавляем урок в локальный массив
				console.log(`[handleSaveLesson] Создание нового урока:`, lessonElement)
				this.lessons.push(lessonElement)
				console.log(' Урок добавлен в локальный массив:', lessonElement)
				console.log('📊 Всего уроков в локальном массиве:', this.lessons.length)

				this.$emit('element-created', {
					type: 'lessons',
					element: lessonElement,
				})
				this.showNotification('Урок создан локально', 'success')
			}

			//  Закрываем форму после создания/обновления
			this.showLessonCreation = false
			this.editingMaterial = null
			this.editingMaterialType = null
			this.lessonFormKey++
			this.clearFormState('lesson')

			//  Автоскролл к созданному уроку
			this.$nextTick(() => {
				setTimeout(() => {
					const materialsSection = document.querySelector('.mts-materials')
					if (materialsSection) {
						materialsSection.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						})
					}
				}, 100)
			})
		},

		async handleSavePractice(practiceData) {
			console.log('🔍 Сохранение практического задания:', practiceData)
			console.log('🔍 practiceData.miniTest:', practiceData.miniTest)
			console.log(
				'🔍 practiceData.includeMiniTest:',
				practiceData.includeMiniTest
			)
			console.log('📍 Режим работы:', this.isLocalMode ? 'ЛОКАЛЬНЫЙ' : 'БД')

			if (this.isTempModuleId()) {
				this.showNotification('Ошибка: Сначала сохраните модуль, затем добавляйте элементы', 'error')
				return
			}

			//  Проверяем, редактируем ли мы существующее задание или создаем новое
			const isEditMode =
				practiceData.id !== undefined && practiceData.id !== null

			//  Создаем/обновляем практическое задание
			const practiceElement = {
				id: isEditMode ? practiceData.id : Date.now(),
				title: practiceData.title || 'Новое практическое задание',
				content:
					practiceData.content ||
					practiceData.description ||
					practiceData.goal ||
					'',
				description:
					practiceData.content ||
					practiceData.description ||
					practiceData.goal ||
					'',
				hasLocalChanges: true,
				instructions: practiceData.instructions || '',
				due_date: practiceData.dueDate || null,
				due_time: practiceData.dueTime || null,
				max_score: practiceData.maxScore || 100,
				materials: practiceData.materials || [],
				deletedMaterials: practiceData.deletedMaterials || [], //  Добавлено для отслеживания удаленных файлов
				is_published: true,
				is_draft: false,
				is_hidden: false,
				//  Добавляем fullData для сохранения в БД через saveLocalMaterialsToDB
				//  ИСПРАВЛЕНО: Сохраняем ВСЕ поля для корректного редактирования
				fullData: {
					id: isEditMode ? practiceData.id : Date.now(), //  Добавлен id
					title: practiceData.title || 'Новое практическое задание',
					module: practiceData.module || this.moduleId, //  Добавлен module
					module_id: practiceData.module || this.moduleId, //  Добавлен module_id для БД
					includeMiniTest: practiceData.includeMiniTest || false, //  Добавлен includeMiniTest
					content:
						practiceData.content ||
						practiceData.description ||
						practiceData.goal ||
						'',
					questions: practiceData.questions || [], //  Добавлен questions
					description:
						practiceData.content ||
						practiceData.description ||
						practiceData.goal ||
						'',
					instructions: practiceData.instructions || '',
					dueDate: practiceData.dueDate || null,
					dueTime: practiceData.dueTime || null,
					maxScore: practiceData.maxScore || 100,
					materials: practiceData.materials || [],
					deletedMaterials: practiceData.deletedMaterials || [], //  Добавлено
					miniTest: practiceData.miniTest || null, //  Добавлен miniTest
					mini_test: practiceData.miniTest || null, //  Добавлен mini_test для БД
					is_published: true, //  Добавлено - сразу публикуем
					is_draft: false, //  Добавлено - не черновик
					hasLocalChanges: true, //  Флаг локальных изменений
				},
			}

			if (isEditMode) {
				//  ВСЕГДА обновляем задание в локальном массиве (для сохранения локальных изменений)
				const index = this.practices.findIndex(p => p.id === practiceElement.id)
				if (index !== -1) {
					//  ВАЖНО: Обновляем объект, сохраняя ссылку
					Object.assign(this.practices[index], practiceElement)
					console.log(
						' Практическое задание обновлено в локальном массиве:',
						this.practices[index]
					)
					console.log(
						'🔍 fullData после обновления:',
						this.practices[index].fullData
					)
				}

				this.$emit('element-updated', {
					type: 'practice',
					element: practiceElement,
				})
				this.showNotification(
					'Практическое задание обновлено локально',
					'success'
				)
			} else {
				//  ВСЕГДА добавляем практическое задание в локальный массив
				this.practices.push(practiceElement)
				console.log(
					' Практическое задание добавлено в локальный массив:',
					practiceElement
				)
				console.log(
					'📊 Всего практических заданий в локальном массиве:',
					this.practices.length
				)

				this.$emit('element-created', {
					type: 'practice',
					element: practiceElement,
				})
				this.showNotification(
					'Практическое задание создано локально',
					'success'
				)
			}

			//  Закрываем форму после создания/обновления
			this.showPracticeCreation = false
			this.editingMaterial = null
			this.editingMaterialType = null
			this.practiceFormKey++

			//  Автоскролл к созданному заданию
			this.$nextTick(() => {
				setTimeout(() => {
					const materialsSection = document.querySelector('.mts-materials')
					if (materialsSection) {
						materialsSection.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						})
					}
				}, 100)
			})
		},

		async handleSaveHomework(homeworkData) {
			console.log('🔍 Сохранение домашнего задания:', homeworkData)
			console.log('📍 Режим работы:', this.isLocalMode ? 'ЛОКАЛЬНЫЙ' : 'БД')

			if (this.isTempModuleId()) {
				this.showNotification('Ошибка: Сначала сохраните модуль, затем добавляйте элементы', 'error')
				return
			}

			//  Проверяем, редактируем ли мы существующее задание или создаем новое
			const isEditMode =
				homeworkData.id !== undefined && homeworkData.id !== null

			//  Создаем/обновляем домашнее задание
			//  Определяем статус черновика из входных данных
			const isDraft =
				homeworkData.isDraft === true || homeworkData.is_draft === true

			const homeworkElement = {
				id: isEditMode ? homeworkData.id : Date.now(),
				title: homeworkData.title || 'Новое домашнее задание',
				description: homeworkData.description || '',
				hasLocalChanges: true,
				requirements: homeworkData.requirements || '',
				due_date: homeworkData.dueDate || null,
				due_time: homeworkData.dueTime || null,
				max_score: homeworkData.maxScore ?? null,
				materials: homeworkData.materials || [],
				is_published: !isDraft,
				is_draft: isDraft,
				is_hidden: false,
				//  Добавляем fullData для сохранения в БД через saveLocalMaterialsToDB
				//  ИСПРАВЛЕНО: Сохраняем ВСЕ поля для корректного редактирования
				fullData: {
					id: isEditMode ? homeworkData.id : Date.now(),
					title: homeworkData.title || 'Новое домашнее задание',
					description: homeworkData.description || '',
					module: homeworkData.module || this.moduleId,
					publishType: homeworkData.publishType || 'immediate',
					publishDate: homeworkData.publishDate || '',
					publishTime: homeworkData.publishTime || '',
					allowLateSubmission: homeworkData.allowLateSubmission ?? false,
					requirements: homeworkData.requirements || [],
					dueDate: homeworkData.dueDate || null,
					dueTime: homeworkData.dueTime || null,
					maxScore: homeworkData.maxScore ?? null,
					materials: homeworkData.materials || [],
					deletedMaterials: homeworkData.deletedMaterials || [],
					isDraft: isDraft,
					is_draft: isDraft,
					hasLocalChanges: true, //  Флаг локальных изменений
				},
			}

			if (isEditMode) {
				//  ВСЕГДА обновляем задание в локальном массиве (для сохранения локальных изменений)
				const index = this.homeworks.findIndex(h => h.id === homeworkElement.id)
				if (index !== -1) {
					this.homeworks[index] = homeworkElement
					console.log(
						' Домашнее задание обновлено в локальном массиве:',
						homeworkElement
					)
				}

				this.$emit('element-updated', {
					type: 'homework',
					element: homeworkElement,
				})
				this.showNotification('Домашнее задание обновлено локально', 'success')
			} else {
				//  ВСЕГДА добавляем домашнее задание в локальный массив
				this.homeworks.push(homeworkElement)
				console.log(
					' Домашнее задание добавлено в локальный массив:',
					homeworkElement
				)
				console.log(
					'📊 Всего домашних заданий в локальном массиве:',
					this.homeworks.length
				)

				this.$emit('element-created', {
					type: 'homework',
					element: homeworkElement,
				})
				this.showNotification('Домашнее задание создано локально', 'success')
			}

			//  Закрываем форму после создания/обновления
			this.showHomeworkCreation = false
			this.editingMaterial = null
			this.editingMaterialType = null
			this.homeworkFormKey++

			//  Автоскролл к созданному заданию
			this.$nextTick(() => {
				setTimeout(() => {
					const materialsSection = document.querySelector('.mts-materials')
					if (materialsSection) {
						materialsSection.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
						})
					}
				}, 100)
			})
		},

		//  УЛУЧШЕННЫЙ МЕТОД СОХРАНЕНИЯ ЧЕРНОВИКА
		handleSaveDraft(draftData) {
			console.log('📝 Сохранение черновика:', draftData)

			const materialType = this.activeTab
			const existingId = draftData.id

			//  Определяем массив материалов и проверяем существующий черновик
			const materialsMap = {
				tests: this.tests,
				lessons: this.lessons,
				practice: this.practices,
				homework: this.homeworks,
			}
			const materials = materialsMap[materialType]

			//  Ищем существующий материал по ID
			let existingIndex = -1
			let existingMaterial = null
			if (existingId) {
				existingIndex = materials.findIndex(m => m.id === existingId)
				existingMaterial = existingIndex !== -1 ? materials[existingIndex] : null
			}

			//  КЛЮЧЕВАЯ ЛОГИКА:
			// - Если материал УЖЕ черновик (isDraft/is_draft) — обновляем его
			// - Если материал опубликован — создаём НОВЫЙ черновик (копию)
			const isExistingDraft = existingMaterial && (existingMaterial.isDraft || existingMaterial.is_draft)
			const isUpdate = isExistingDraft
			const draftId = isUpdate ? existingId : Date.now()

			console.log(`📋 Режим: ${isUpdate ? 'ОБНОВЛЕНИЕ черновика' : 'СОЗДАНИЕ нового черновика'}, ID: ${draftId}`)

			const draft = {
				id: draftId,
				type: materialType,
				title: draftData.title || draftData.testName || 'Новый черновик',
				isDraft: true,
				is_draft: true,
				isHidden: false,
				is_hidden: false,
				fullData: { ...draftData, id: draftId, isDraft: true, is_draft: true },
			}

			// Добавляем специфичные поля в зависимости от типа
			if (materialType === 'lessons') {
				const enableTocValue =
					draftData.enableToc ?? draftData.enable_toc ?? false
				const tocModeValue = draftData.tocMode ?? draftData.toc_mode ?? 'auto'
				const disableMainMaterialsDownloadValue =
					draftData.disableMainMaterialsDownload ??
					draftData.disable_main_materials_download ??
					false
				const disableAdditionalMaterialsDownloadValue =
					draftData.disableAdditionalMaterialsDownload ??
					draftData.disable_additional_materials_download ??
					false

				Object.assign(draft, {
					type: draftData.type || 'video',
					description: draftData.description || '',
					duration: draftData.duration || 30,
					content: draftData.content || draftData.articleContent || '',
					video_url: draftData.videoUrl || '',
					cover_image_url:
						draftData.videoCoverUrl ||
						draftData.cover_image_url ||
						draftData.cover_image ||
						null,
					materials: draftData.materials || [],
					enable_toc: enableTocValue,
					enableToc: enableTocValue,
					toc_mode: tocModeValue,
					tocMode: tocModeValue,
					disable_main_materials_download: disableMainMaterialsDownloadValue,
					disableMainMaterialsDownload: disableMainMaterialsDownloadValue,
					disable_additional_materials_download:
						disableAdditionalMaterialsDownloadValue,
					disableAdditionalMaterialsDownload:
						disableAdditionalMaterialsDownloadValue,
				})
				draft.fullData = {
					...draftData,
					id: draftId,
					type: draftData.type || 'video',
					title: draftData.title,
					description: draftData.description || '',
					content: draftData.content || draftData.articleContent || '',
					articleContent: draftData.articleContent || draftData.content || '',
					videoUrl: draftData.videoUrl || '',
					videoFile: draftData.videoFile || null,
					videoCoverFile: draftData.videoCoverFile || null,
					videoCoverUrl:
						draftData.videoCoverUrl ||
						draftData.cover_image_url ||
						draftData.cover_image ||
						'',
					cover_image_url:
						draftData.videoCoverUrl ||
						draftData.cover_image_url ||
						draftData.cover_image ||
						null,
					docFile: draftData.docFile || null,
					materials: draftData.materials || [],
					deletedMaterials: draftData.deletedMaterials || [],
					duration: draftData.duration || 30,
					enable_toc: enableTocValue,
					enableToc: enableTocValue,
					toc_mode: tocModeValue,
					tocMode: tocModeValue,
					disable_main_materials_download: disableMainMaterialsDownloadValue,
					disableMainMaterialsDownload: disableMainMaterialsDownloadValue,
					disable_additional_materials_download:
						disableAdditionalMaterialsDownloadValue,
					disableAdditionalMaterialsDownload:
						disableAdditionalMaterialsDownloadValue,
					isDraft: true,
					is_draft: true,
				}
			} else if (materialType === 'tests') {
				Object.assign(draft, {
					duration: draftData.duration || 0,
					questionsCount: (draftData.questions || []).length,
					passingScore: `${draftData.passingScore || 70}%`,
					attempts: this.formatAttempts(draftData.attempts || 1),
				})
				draft.fullData = {
					...draftData,
					id: draftId,
					isDraft: true,
					is_draft: true,
				}
			} else if (materialType === 'practice') {
				Object.assign(draft, {
					description:
						draftData.description || draftData.content || draftData.goal || '',
				})
				draft.fullData = {
					...draftData,
					id: draftId,
					isDraft: true,
					is_draft: true,
				}
			} else if (materialType === 'homework') {
				Object.assign(draft, {
					description: draftData.description || '',
					due_date: draftData.dueDate || null,
					due_time: draftData.dueTime || null,
				})
				draft.fullData = {
					...draftData,
					id: draftId,
					isDraft: true,
					is_draft: true,
				}
			}

			//  Обновляем или добавляем черновик
			if (isUpdate) {
				// Обновляем существующий черновик
				materials.splice(existingIndex, 1, draft)
				console.log(` Черновик обновлен: ${materialType}`, draft)
				this.$emit('element-updated', { type: materialType, element: draft })
				this.showNotification('Черновик обновлен!', 'info')
			} else {
				// Добавляем новый черновик
				materials.push(draft)
				console.log(` Черновик создан: ${materialType}`, draft)
				this.$emit('element-created', { type: materialType, element: draft })
				this.showNotification('Черновик сохранен!', 'info')
			}

			// Сохраняем в localStorage
			this.saveToLocalStorage()

			this.$emit('draft-saved', draftData)

			//  Закрываем форму после сохранения черновика
			switch (materialType) {
				case 'tests':
					this.showTestCreation = false
					this.testFormKey++
					break
				case 'lessons':
					this.showLessonCreation = false
					this.lessonFormKey++
					break
				case 'practice':
					this.showPracticeCreation = false
					this.practiceFormKey++
					break
				case 'homework':
					this.showHomeworkCreation = false
					this.homeworkFormKey++
					break
			}
			this.editingMaterial = null
			this.editingMaterialType = null

			//  Автоскролл к секции черновиков
			this.$nextTick(() => {
				setTimeout(() => {
					const draftsSection = document.querySelector('.mts-drafts')
					if (draftsSection) {
						draftsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
					}
				}, 100)
			})
		},

		handleCancelTestCreation(payload) {
			console.log('Отмена создания теста', payload)
			this.showTestCreation = false
			this.editingMaterial = null
			this.editingMaterialIndex = null
			this.clearFormState('test')

			//  Скролл к элементу если есть ID
			if (payload?.scrollToId) {
				this.scrollToMaterial(payload.scrollToId, 'test')
			}
		},

		handleCancelLessonCreation(payload) {
			console.log('Отмена создания урока', payload)
			this.showLessonCreation = false
			this.editingMaterial = null
			this.editingMaterialIndex = null
			this.clearFormState('lesson')

			//  Скролл к элементу если есть ID
			if (payload?.scrollToId) {
				this.scrollToMaterial(payload.scrollToId, 'lesson')
			}
		},

		handleCancelPracticeCreation(payload) {
			console.log('Отмена создания практического задания', payload)
			this.showPracticeCreation = false
			this.editingMaterial = null
			this.editingMaterialIndex = null
			this.clearFormState('practice')

			//  Скролл к элементу если есть ID
			if (payload?.scrollToId) {
				this.scrollToMaterial(payload.scrollToId, 'practice')
			}
		},

		handleCancelHomeworkCreation(payload) {
			console.log('Отмена создания домашнего задания', payload)
			this.showHomeworkCreation = false
			this.editingMaterial = null
			this.editingMaterialIndex = null
			this.clearFormState('homework')

			//  Скролл к элементу если есть ID
			if (payload?.scrollToId) {
				this.scrollToMaterial(payload.scrollToId, 'homework')
			}
		},

		//  УЛУЧШЕННЫЙ МЕТОД РЕДАКТИРОВАНИЯ
		// В handleEditMaterial (строка ~1145)
		async handleEditMaterial(data) {
			console.log('Редактирование материала:', data)
			console.log('📍 Режим работы:', this.isLocalMode ? 'ЛОКАЛЬНЫЙ' : 'БД')
			console.log('📍 activeTab:', this.activeTab)
			console.log('📍 data.type:', data.type)

			// Определяем источник данных
			let materials
			if (this.isLocalMode && this.localModule && this.localModule.elements) {
				if (!this.localModule.elements[this.activeTab]) {
					this.localModule.elements[this.activeTab] = []
				}
				materials = this.localModule.elements[this.activeTab]
			} else {
				const materialsMap = {
					lessons: this.lessons,
					practice: this.practices,
					homework: this.homeworks,
					tests: this.tests,
				}
				materials = materialsMap[data.type || this.activeTab]
			}

			if (!materials) {
				console.error('❌ Массив материалов не найден')
				return
			}

			const index = materials.findIndex(m => m.id === data.id)
			if (index === -1) {
				console.error('❌ Материал не найден в массиве:', data.id)
				return
			}

			const material = materials[index]
			console.log('✏️ Найден материал для редактирования:', material)

			this.editingMaterialIndex = index
			this.editingMaterialType = data.type || this.activeTab

			//  ИСПРАВЛЕНО: Проверяем флаг на верхнем уровне И в fullData
			const hasLocalChanges =
				material.hasLocalChanges === true ||
				material.fullData?.hasLocalChanges === true

			// Если есть fullData, используем его, иначе используем сам материал
			this.editingMaterial = material.fullData
				? { ...material.fullData }
				: { ...material }

			console.log('📝 editingMaterial установлен:', this.editingMaterial)
			console.log('📝 isScheduledPublish:', this.editingMaterial.isScheduledPublish)
			console.log('📝 autoPublish:', this.editingMaterial.autoPublish)
			console.log('📝 publishDate:', this.editingMaterial.publishDate)
			console.log('📝 publishTime:', this.editingMaterial.publishTime)
			console.log('📝 scheduled_publish_at:', this.editingMaterial.scheduled_publish_at)

			//  ИСПРАВЛЕНО: Загружаем из БД ТОЛЬКО если НЕТ локальных изменений
			if (this.editingMaterialType === 'tests') {
				const testId = this.editingMaterial.id

				if (hasLocalChanges) {
					console.log(
						' Используем локальные изменения теста (не загружаем из БД)'
					)
				} else if (testId && testId < 1000000000000) {
					// Загружаем из БД только если нет локальных изменений
					try {
						console.log('📥 Загрузка данных теста из БД:', testId)
						const { testsService } = await import('@/services')
						const testFromDB = await testsService.getWithQuestions(testId)
						if (testFromDB) {
							const mappedQuestions = (testFromDB.questions || []).map(
								(q, idx) => ({
									id: q.id,
									text: q.question_text || q.text || '',
									type:
										q.question_type === 'single_choice'
											? 'single'
											: q.question_type === 'multiple_choice'
											? 'multiple'
											: q.question_type || q.type || 'single',
									points: q.points ?? 1,
									explanation: q.explanation || '',
									order_number: q.order_number ?? idx + 1,
									correctText:
										q.correct_text ??
										q.correctText ??
										q.correct_answer ??
										'',
									caseSensitive:
										q.case_sensitive ?? q.caseSensitive ?? false,
									image:
										q.image_url ?? q.image ?? q.imageUrl ?? null,
									imageFile: null,
									isPersisted: true,
									answers: (q.answers || []).map(a => ({
										id: a.id,
										text: a.answer_text || a.text || '',
										isCorrect: a.is_correct ?? a.isCorrect ?? false,
										order_number: a.order_number,
										image:
											a.image_url ?? a.image ?? a.imageUrl ?? null,
										imageFile: null,
										isPersisted: true,
									})),
								})
							)

							this.editingMaterial = {
								...this.editingMaterial,
								id: testFromDB.id,
								title: testFromDB.title,
								description: testFromDB.description,
								duration: testFromDB.duration_minutes ?? 0,
								passingScore: testFromDB.passing_score ?? 70,
								attempts: testFromDB.max_attempts ?? 1,
								randomOrder: testFromDB.random_order ?? false,
								showCorrectAnswers: testFromDB.show_correct_answers ?? true,
								allowReview: testFromDB.allow_review ?? true,
								penalizeWrong: testFromDB.penalize_wrong ?? false,
								penaltyPercentage: testFromDB.penalty_percentage ?? 50,
								disableCopying: testFromDB.disable_copying ?? false,
								limitTimePerQuestion:
									testFromDB.limit_time_per_question ?? false,
								timePerQuestion: testFromDB.time_per_question ?? 0,
								selectRandomQuestions:
									testFromDB.select_random_questions ?? false,
								questionsToShow: testFromDB.questions_to_show ?? 0,
								questions: mappedQuestions,
								is_published: testFromDB.is_published,
								is_draft: testFromDB.is_draft,
								is_hidden: testFromDB.is_hidden ?? false,
								scheduled_publish_at: testFromDB.scheduled_publish_at || null,
								// Автопубликация - вычисляем из scheduled_publish_at
								isScheduledPublish: !!testFromDB.scheduled_publish_at,
								publishDate: testFromDB.scheduled_publish_at
									? testFromDB.scheduled_publish_at.split(' ')[0]
									: '',
								publishTime: testFromDB.scheduled_publish_at
									? testFromDB.scheduled_publish_at.split(' ')[1]?.slice(0, 5)
									: '',
							}
							console.log(
								' Данные теста загружены из БД:',
								this.editingMaterial
							)
						}
					} catch (error) {
						console.error('❌ Ошибка загрузки теста:', error)
					}
				} else {
					console.log(
						'📋 Локальный тест, данные из fullData:',
						this.editingMaterial
					)
				}
			}

			//  ИСПРАВЛЕНО: Аналогично для уроков
			if (this.editingMaterialType === 'lessons') {
				const lessonId = this.editingMaterial.id

				if (hasLocalChanges) {
					console.log(
						' Используем локальные изменения урока (не загружаем из БД)'
					)
				} else if (lessonId && lessonId < 1000000000000) {
					try {
						console.log('📥 Загрузка данных урока из БД:', lessonId)
						const { lessonsService } = await import('@/services')
						const lessonFromDB = await lessonsService.getById(lessonId)
						if (lessonFromDB) {
							let parsedMaterials = lessonFromDB.additional_materials || []
							if (typeof parsedMaterials === 'string') {
								try {
									parsedMaterials = JSON.parse(parsedMaterials)
								} catch {
									parsedMaterials = []
								}
							}
							if (!Array.isArray(parsedMaterials)) {
								parsedMaterials = []
							}

							const enableTocValue =
								lessonFromDB.enable_toc ?? lessonFromDB.enableToc ?? false
							const tocModeValue =
								lessonFromDB.toc_mode ?? lessonFromDB.tocMode ?? 'auto'
							const disableMainMaterialsDownloadValue =
								lessonFromDB.disable_main_materials_download ??
								lessonFromDB.disableMainMaterialsDownload ??
								false
							const disableAdditionalMaterialsDownloadValue =
								lessonFromDB.disable_additional_materials_download ??
								lessonFromDB.disableAdditionalMaterialsDownload ??
								false

							// Парсинг scheduled_publish_at для формы редактирования
							const scheduledAt = lessonFromDB.scheduled_publish_at || null
							let publishDate = ''
							let publishTime = ''
							let publishType = 'immediate'

							console.log('🔍 [handleEditMaterial] Парсинг scheduled_publish_at:', {
								scheduledAt,
								lessonId: lessonFromDB.id,
								is_published: lessonFromDB.is_published
							})

							if (scheduledAt) {
								publishType = 'scheduled'
								const date = this.parseScheduledAtToDate(scheduledAt)
								if (date) {
									// ISO формат "2025-12-24T11:30:00.000Z" или "YYYY-MM-DD HH:MM"
									const year = date.getFullYear()
									const month = String(date.getMonth() + 1).padStart(2, '0')
									const day = String(date.getDate()).padStart(2, '0')
									const hours = String(date.getHours()).padStart(2, '0')
									const minutes = String(date.getMinutes()).padStart(2, '0')
									publishDate = `${year}-${month}-${day}`
									publishTime = `${hours}:${minutes}`
								} else {
									console.warn(
										'⚠️ [handleEditMaterial] Не удалось распарсить scheduled_publish_at:',
										scheduledAt
									)
								}
								console.log(' [handleEditMaterial] Распарсено:', {
									publishType,
									publishDate,
									publishTime
								})
							} else if (lessonFromDB.is_published) {
								publishType = 'immediate'
							}

							this.editingMaterial = {
								...this.editingMaterial,
								id: lessonFromDB.id,
								title: lessonFromDB.title,
								description: lessonFromDB.description || '',
								type: lessonFromDB.lesson_type || 'video',
								duration: lessonFromDB.duration_minutes ?? 30,
								videoUrl: lessonFromDB.video_url || '',
								videoCoverUrl:
									lessonFromDB.cover_image_url ||
									lessonFromDB.cover_image ||
									'',
								cover_image_url:
									lessonFromDB.cover_image_url ||
									lessonFromDB.cover_image ||
									null,
								articleContent: lessonFromDB.article_content || '',
								content: lessonFromDB.article_content || '',
								materials: parsedMaterials,
								doc_file_path: lessonFromDB.doc_file_path || null,
								doc_file_name: lessonFromDB.doc_file_name || null,
								doc_file_size: lessonFromDB.doc_file_size || null,
								enable_toc: enableTocValue,
								enableToc: enableTocValue,
								toc_mode: tocModeValue,
								tocMode: tocModeValue,
								disable_main_materials_download:
									disableMainMaterialsDownloadValue,
								disableMainMaterialsDownload: disableMainMaterialsDownloadValue,
								disable_additional_materials_download:
									disableAdditionalMaterialsDownloadValue,
								disableAdditionalMaterialsDownload:
									disableAdditionalMaterialsDownloadValue,
								publishType: publishType,
								publishDate: publishDate,
								publishTime: publishTime,
								scheduled_publish_at: scheduledAt,
								is_published: lessonFromDB.is_published,
								is_draft: lessonFromDB.is_draft,
							}
							console.log(
								' Данные урока загружены из БД:',
								this.editingMaterial
							)
						}
					} catch (error) {
						console.error('❌ Ошибка загрузки урока:', error)
					}
				} else {
					console.log(
						'📋 Локальный урок, данные из fullData:',
						this.editingMaterial
					)
				}
			}

			//  ИСПРАВЛЕНО: Аналогично для практики
			if (this.editingMaterialType === 'practice') {
				const practiceId = this.editingMaterial.id

				if (hasLocalChanges) {
					console.log(
						' Используем локальные изменения практического задания (не загружаем из БД)'
					)
				} else if (practiceId && practiceId < 1000000000000) {
					try {
						console.log(
							'📥 Загрузка данных практического задания из БД:',
							practiceId
						)
						const { practicalService } = await import('@/services')
						const practiceFromDB = await practicalService.getById(practiceId)
						if (practiceFromDB) {
							this.editingMaterial = {
								...this.editingMaterial,
								id: practiceFromDB.id,
								title: practiceFromDB.title,
								content:
									practiceFromDB.content ||
									practiceFromDB.task_description ||
									practiceFromDB.goal ||
									'',
								description:
									practiceFromDB.content ||
									practiceFromDB.task_description ||
									practiceFromDB.goal ||
									'',
								questions: practiceFromDB.questions || [],
								materials: practiceFromDB.materials || [],
								miniTest: practiceFromDB.mini_test || null,
								includeMiniTest: !!practiceFromDB.mini_test,
								maxScore: practiceFromDB.max_score || 100,
								dueDate: practiceFromDB.due_date || null,
								dueTime: practiceFromDB.due_time || null,
								is_published: practiceFromDB.is_published,
								is_draft: practiceFromDB.is_draft,
							}
							console.log(
								' Данные практического задания загружены из БД:',
								this.editingMaterial
							)
						}
					} catch (error) {
						console.error('❌ Ошибка загрузки практического задания:', error)
					}
				} else {
					console.log(
						'📋 Локальное практическое задание, данные из fullData:',
						this.editingMaterial
					)
				}

				if (this.editingMaterial.mini_test) {
					this.editingMaterial.miniTest = this.editingMaterial.mini_test
					this.editingMaterial.includeMiniTest = true
				} else if (this.editingMaterial.miniTest) {
					this.editingMaterial.includeMiniTest = true
				}

				if (
					!this.editingMaterial.materials ||
					!Array.isArray(this.editingMaterial.materials)
				) {
					this.editingMaterial.materials = []
				}
			}

			// Остальной код без изменений...
			this.$nextTick(() => {
				console.log('🎯 [handleEditMaterial] Открытие формы:', {
					editingMaterialType: this.editingMaterialType,
					editingMaterial: this.editingMaterial,
					publishDate: this.editingMaterial?.publishDate,
					publishTime: this.editingMaterial?.publishTime,
					scheduled_publish_at: this.editingMaterial?.scheduled_publish_at
				})

				switch (this.editingMaterialType) {
					case 'tests':
						this.testFormKey++
						this.showTestCreation = true
						this.saveFormState('test', true, this.editingMaterial)
						break
					case 'lessons':
						this.lessonFormKey++
						this.showLessonCreation = true
						this.saveFormState('lesson', true, this.editingMaterial)
						break
					case 'practice':
						this.practiceFormKey++
						this.showPracticeCreation = true
						this.saveFormState('practice', true, this.editingMaterial)
						break
					case 'homework':
						this.homeworkFormKey++
						this.showHomeworkCreation = true
						this.saveFormState('homework', true, this.editingMaterial)
						break
				}
			})

			this.$nextTick(() => {
				setTimeout(() => {
					const classMap = {
						tests: '.mts-test-creation',
						lessons: '.mts-lesson-creation',
						practice: '.mts-practice-creation',
						homework: '.mts-homework-creation',
					}

					const selector = classMap[this.editingMaterialType]
					if (selector) {
						const element = document.querySelector(selector)
						if (element) {
							element.scrollIntoView({
								behavior: 'smooth',
								block: 'start',
							})
							console.log(` Автоскролл к форме редактирования: ${selector}`)
						}
					}
				}, 100)
			})
		},

		handleToggleVisibility(data) {
			console.log('Переключение видимости материала:', data)

			//  ИСПРАВЛЕНО: ищем в исходных массивах, а не в computed
			const materialsMap = {
				lessons: this.lessons,
				practice: this.practices,
				homework: this.homeworks,
				tests: this.tests,
			}

			const materials = materialsMap[this.activeTab]
			if (!materials) return

			const material = materials.find(m => m.id === data.id)
			if (material) {
				//  ИСПРАВЛЕНО: инвертируем текущее значение
				const newHiddenState = !data.isHidden
				material.is_hidden = newHiddenState
				material.isHidden = newHiddenState
				if (material.fullData) {
					material.fullData.is_hidden = newHiddenState
					material.fullData.isHidden = newHiddenState
				}

				// Сохраняем в localStorage
				this.saveToLocalStorage()

				this.showNotification(
					newHiddenState ? 'Материал скрыт' : 'Материал отображается',
					'info'
				)

				this.$emit('material-visibility-changed', {
					id: data.id,
					isHidden: newHiddenState,
					type: this.activeTab,
				})
			}
		},

		async handleDeleteMaterial(data) {
			console.log('Удаление материала:', data)
			console.log('📍 Режим работы:', this.isLocalMode ? 'ЛОКАЛЬНЫЙ' : 'БД')

			// 1. Берём правильный массив материалов
			let materials
			if (this.isLocalMode && this.localModule && this.localModule.elements) {
				// Локальный режим – работаем с localModule.elements
				if (!this.localModule.elements[this.activeTab]) {
					this.localModule.elements[this.activeTab] = []
				}
				materials = this.localModule.elements[this.activeTab]
			} else {
				// Режим БД – работаем с локальными списками компонента
				const materialsMap = {
					lessons: this.lessons,
					practice: this.practices,
					homework: this.homeworks,
					tests: this.tests,
				}
				materials = materialsMap[this.activeTab]
			}

			if (!materials) {
				console.error('❌ Массив материалов не найден')
				return
			}

			const index = materials.findIndex(m => m.id === data.id)
			if (index === -1) {
				console.error('❌ Материал не найден в массиве:', data.id)
				return
			}

			// 2. Удаляем из БД ТОЛЬКО если НЕ локальный режим
			if (!this.isLocalMode && data.id < 1000000000000) {
				try {
					console.log(`🗑️ Удаление материала из БД: ${data.id}`)

					const {
						lessonsService,
						testsService,
						practicalService,
						homeworkService,
					} = await import('@/services')

					const serviceMap = {
						lessons: lessonsService,
						practice: practicalService,
						homework: homeworkService,
						tests: testsService,
					}

					const service = serviceMap[this.activeTab]
					if (service) {
						await service.delete(data.id)
						console.log(` Материал ${data.id} успешно удален из БД`)
					}
				} catch (error) {
					console.error('❌ Ошибка удаления материала из БД:', error)
					this.showNotification(
						'Ошибка удаления материала: ' + error.message,
						'error'
					)
					return
				}
			}

			// 3. Удаляем из локального массива (для UI) – всегда
			materials.splice(index, 1)
			console.log(
				` Материал удален из массива. Осталось материалов: ${materials.length}`
			)

			//  Очищаем форму только если этот материал редактируется
			if (this.editingMaterial && this.editingMaterial.id === data.id) {
				const formKey =
					this.activeTab === 'tests'
						? 'test'
						: this.activeTab === 'lessons'
						? 'lesson'
						: this.activeTab === 'practice'
						? 'practice'
						: 'homework'

				this.clearFormState(formKey)
				this.editingMaterial = null
				this.editingMaterialType = null

				console.log(
					`🗑️ Очищено состояние формы и editingMaterial для удалённого материала`
				)
			}

			// 4. Сохраняем и показываем уведомление
			this.saveToLocalStorage()
			this.showNotification('Материал удален', 'success')

			// 5. Эмитим события
			this.$emit('element-deleted', {
				type: this.activeTab,
				elementId: data.id,
			})

			this.$emit('material-deleted', data)
		},

		handleCloseEditModal() {
			this.showEditModal = false
			this.editingMaterial = null
			this.editingMaterialType = null
			this.editingMaterialIndex = null
		},

		handleSaveEditedMaterial(data) {
			console.log('Сохранение отредактированного материала:', data)

			const material = this.currentMaterials.find(m => m.id === data.id)
			if (material) {
				Object.assign(material, data)
				material.isDraft = false

				// Сохраняем в localStorage
				this.saveToLocalStorage()

				// Преобразуем тип из множественного числа в единственное
				const typeMap = {
					tests: 'test',
					lessons: 'lesson',
					practice: 'practice',
					homework: 'homework',
				}

				this.$emit('element-updated', {
					type: typeMap[this.activeTab] || this.activeTab,
					element: material,
				})
			}

			this.handleCloseEditModal()
			this.$emit('material-updated', data)
		},

		handleSaveEditedDraft(data) {
			console.log('Сохранение отредактированного материала как черновик:', data)

			//  ИСПРАВЛЕНО: ищем в исходных массивах, а не в computed
			const materialsMap = {
				lessons: this.lessons,
				practice: this.practices,
				homework: this.homeworks,
				tests: this.tests,
			}

			const materialType = this.editingMaterialType || this.activeTab
			const materials = materialsMap[materialType]
			if (!materials) {
				console.error('❌ Массив материалов не найден для типа:', materialType)
				this.handleCloseEditModal()
				return
			}

			const existingIndex = materials.findIndex(m => m.id === data.id)
			const existingMaterial = existingIndex !== -1 ? materials[existingIndex] : null

			//  КЛЮЧЕВАЯ ЛОГИКА:
			// - Если материал УЖЕ черновик — обновляем его
			// - Если материал опубликован — создаём НОВЫЙ черновик
			const isExistingDraft = existingMaterial && (existingMaterial.isDraft || existingMaterial.is_draft)

			if (isExistingDraft && existingIndex !== -1) {
				// Обновляем существующий черновик
				Object.assign(materials[existingIndex], data, {
					isDraft: true,
					is_draft: true,
				})
				if (materials[existingIndex].fullData) {
					Object.assign(materials[existingIndex].fullData, data, {
						isDraft: true,
						is_draft: true,
					})
				}
				console.log(' Черновик обновлён:', data.id)
				this.showNotification('Черновик обновлён', 'info')
			} else {
				// Создаём новый черновик (копию опубликованного материала)
				const newDraftId = Date.now()
				const newDraft = {
					...data,
					id: newDraftId,
					isDraft: true,
					is_draft: true,
					fullData: {
						...data,
						id: newDraftId,
						isDraft: true,
						is_draft: true,
					},
				}
				materials.push(newDraft)
				console.log(' Создан новый черновик:', newDraftId)
				this.showNotification('Черновик создан', 'info')
			}

			// Сохраняем в localStorage
			this.saveToLocalStorage()

			this.handleCloseEditModal()
			this.$emit('material-draft-updated', data)
		},
		handlePreviewIntent() {
			this.$emit('preview-opening')
		},

		handlePreviewMaterial(data) {
			if (!data || !['lessons', 'practice', 'homework', 'tests'].includes(data.type)) return

			const moduleId = this.currentModuleId || this.moduleId
			const materialId = data.id
			const hasValidModuleId =
				moduleId !== null &&
				moduleId !== undefined &&
				moduleId !== '' &&
				!String(moduleId).startsWith('temp_')
			const hasValidMaterialId =
				materialId !== null &&
				materialId !== undefined &&
				materialId !== '' &&
				!String(materialId).startsWith('temp_')

			if (!hasValidModuleId || !hasValidMaterialId) {
				const typeLabelMap = {
					lessons: 'урок',
					practice: 'практику',
					homework: 'домашнее задание',
					tests: 'тест',
				}
				const label = typeLabelMap[data.type] || 'материал'
				this.showNotification(
					`Сначала сохраните модуль и ${label}, затем откройте предпросмотр.`,
					'error',
				)
				return
			}

			// Устанавливаем флаг для Bitrix, что открываем preview
			const isBitrix = localStorage.getItem('bitrix24_mode') === 'true'
			if (isBitrix) {
				sessionStorage.setItem('bitrix_preview_opened', 'true')
				console.log('[Bitrix] Флаг preview установлен (материал)')
			}

			const sectionMap = {
				lessons: 'lessons',
				practice: 'practice',
				homework: 'homework',
				tests: 'test',
			}
			const idQueryKeyMap = {
				lessons: 'lessonId',
				practice: 'practiceId',
				homework: 'homeworkId',
				tests: 'testId',
			}
			const query = {
				courseId: this.courseId ? String(this.courseId) : undefined,
				editorContext: this.$route.path.startsWith('/admin/') ? 'admin' : 'teacher',
				moduleId: String(moduleId),
				previewType: data.type === 'lessons' ? 'lesson' : 'module',
				section: sectionMap[data.type],
				testMode: '1',
			}
			const idKey = idQueryKeyMap[data.type]
			if (idKey) {
				query[idKey] = String(materialId)
			}

			openInternalRouteInContext(this.$router, {
				name: 'TeacherScreen8Preview',
				query,
			}, {
				title: 'Предпросмотр материала',
				leftBoundary: 48,
				closeCallback: () => this.$emit('preview-closed'),
			})
		},

		//  НОВЫЙ МЕТОД: Загрузка всех материалов из БД
		async loadAllMaterialsFromDB() {
			if (!this.moduleId) {
				console.warn('⚠️ moduleId не определен, пропускаем загрузку материалов')
				return
			}

			//  Проверка: если модуль новый (временный ID), не загружаем материалы из БД
			if (this.isTempModuleId()) {
				console.log(
					`⏭️ Модуль ${this.moduleId} имеет временный ID, пропускаем загрузку материалов из БД`
				)
				// Очищаем списки материалов
				this.lessons = []
				this.tests = []
				this.practices = []
				this.homeworks = []
				return
			}

			console.log(`🔍 Загрузка материалов для модуля ${this.moduleId}`)

			try {
				const {
					lessonsService,
					testsService,
					practicalService,
					homeworkService,
					modulesService,
				} = await import('@/services')

				//  Загружаем информацию о модуле
				try {
					this.currentModule = await modulesService.getById(this.moduleId)
					console.log(' Информация о модуле загружена:', this.currentModule)
				} catch (error) {
					console.warn('⚠️ Не удалось загрузить информацию о модуле:', error)
				}

				// Загружаем все типы материалов параллельно
				const [lessonsData, testsData, practicalData, homeworkData] =
					await Promise.all([
						lessonsService.getByModuleId(this.moduleId).catch(err => {
							console.error('Ошибка загрузки уроков:', err)
							return []
						}),
						testsService.getByModuleId(this.moduleId).catch(err => {
							console.error('Ошибка загрузки тестов:', err)
							return []
						}),
						practicalService.getByModuleId(this.moduleId).catch(err => {
							console.error('Ошибка загрузки практических заданий:', err)
							return []
						}),
						homeworkService.getByModuleId(this.moduleId).catch(err => {
							console.error('Ошибка загрузки домашних заданий:', err)
							return []
						}),
					])

				// Форматируем уроки
				this.lessons = (lessonsData || []).map(lesson => {
					console.log(`[loadAllMaterialsFromDB] Загружен урок:`, {
						id: lesson.id,
						title: lesson.title,
						scheduled_publish_at: lesson.scheduled_publish_at,
						is_hidden: lesson.is_hidden,
						is_draft: lesson.is_draft
					})
					
					return {
						id: lesson.id,
						type: 'lessons',
						title: lesson.title,
						duration: lesson.duration,
						//  Исправлено: используем lesson_type вместо type
						lessonType: lesson.lesson_type === 'video' ? 'Видео-урок' : 'Статья',
						isDraft: lesson.is_draft,
						isHidden: lesson.is_hidden,
						//  ДОБАВЛЕНО: передаем scheduled_publish_at
						scheduled_publish_at: lesson.scheduled_publish_at,
						fullData: {
							...lesson,
							//  Добавляем type для совместимости с формой редактирования
							type: lesson.lesson_type,
						},
					}
				})

				console.log(`[loadAllMaterialsFromDB] Уроков загружено: ${this.lessons.length}`, this.lessons)

				// Форматируем тесты
				const sortedTestsData = this.sortMaterialsData(
					testsData,
					['test_id', 'testId'],
					this.tests,
				)

				this.tests = (sortedTestsData || []).map(test => ({
					id: test.id,
					type: 'tests',
					title: test.title,
					description: test.description,
					autoPublish: test.auto_publish
						? 'Сразу'
						: `${test.publish_date} ${test.publish_time}`,
					duration: test.duration,
					questionsCount: test.questions?.length || 0,
					passingScore: `${test.passing_score}%`,
					attempts: this.formatAttempts(test.max_attempts),
					totalPoints: test.total_points || 0,
					isDraft: test.is_draft,
					isHidden: test.is_hidden,
					fullData: test,
				}))

				// Форматируем практические задания
				const sortedPracticesData = this.sortMaterialsData(
					practicalData,
					['task_id', 'taskId', 'practical_id', 'practicalId'],
					this.practices,
				)
				this.practices = (sortedPracticesData || []).map(practice => ({
					id: practice.id,
					type: 'practice',
					title: practice.title,
					dueDate: practice.due_date,
					isDraft: practice.is_draft,
					isHidden: practice.is_hidden,
					fullData: practice,
				}))

				// Форматируем домашние задания
				const sortedHomeworksData = this.sortMaterialsData(
					homeworkData,
					['homework_id', 'homeworkId', 'task_id', 'taskId'],
					this.homeworks,
				)
				this.homeworks = (sortedHomeworksData || []).map(homework => ({
					id: homework.id,
					type: 'homework',
					title: homework.title,
					dueDate: homework.due_date,
					dueTime: homework.due_time,
					isDraft: homework.is_draft,
					isHidden: homework.is_hidden,
					fullData: homework,
				}))

				console.log(' Материалы загружены из БД:', {
					lessons: this.lessons.length,
					tests: this.tests.length,
					practices: this.practices.length,
					homeworks: this.homeworks.length,
				})
			} catch (error) {
				console.error('❌ Ошибка загрузки материалов из БД:', error)
				this.showNotification(
					'Ошибка загрузки материалов. Попробуйте обновить страницу.',
					'error'
				)
			}
		},

		//  СИСТЕМА ПЕРСИСТЕНТНОСТИ ДАННЫХ
		getStorageKey() {
			// Создаем уникальный ключ для каждого модуля
			const moduleKey = this.moduleId || 'default'
			return `moduleTestsSection_module_${moduleKey}`
		},

		async saveToLocalStorage() {
			try {
				const storageKey = this.getStorageKey()
				const dataToSave = {
					moduleId: this.moduleId,
					courseId: this.courseId,
					lessons: this.lessons,
					practices: this.practices,
					homeworks: this.homeworks,
					tests: this.tests,
					timestamp: Date.now(),
					version: '1.0', // Версия структуры данных для будущей миграции
				}

				try {
					localStorage.setItem(storageKey, JSON.stringify(dataToSave))
				} catch (error) {
					console.error(
						'❌ Ошибка сохранения данных модуля в localStorage:',
						error
					)
					throw error
				}
				console.log(
					` Данные модуля ${this.moduleId} сохранены в localStorage`
				)

				//  СОХРАНЯЕМ СВЯЗЬ МОДУЛЯ С КУРСОМ
				if (this.courseId && this.moduleId) {
					const { modulesService } = await import('@/services')

					// Получаем текущий список ID модулей курса
					const courseModulesKey = `course_${this.courseId}_modules`
					let existingModuleIds = []
					try {
						const raw = localStorage.getItem(courseModulesKey)
						const parsed = JSON.parse(raw || '[]')
						existingModuleIds = Array.isArray(parsed) ? parsed : []
					} catch (error) {
						console.warn(
							`Не удалось прочитать список модулей курса ${this.courseId} из localStorage:`,
							error
						)
						existingModuleIds = []
					}

					// Добавляем ID модуля, если его еще нет
					const moduleIdToStore = Number.isInteger(Number(this.moduleId))
						? Number(this.moduleId)
						: this.moduleId
					const hasModuleId = existingModuleIds.some(
						id => String(id) === String(moduleIdToStore)
					)
					if (!hasModuleId) {
						existingModuleIds.push(moduleIdToStore)
						try {
							modulesService.saveCourseModuleIds(this.courseId, existingModuleIds)
						} catch (error) {
							console.warn(
								`Не удалось сохранить список модулей курса ${this.courseId} в localStorage:`,
								error
							)
						}
						console.log(
							` Модуль ${this.moduleId} добавлен в список модулей курса ${this.courseId}`
						)
					}
				}
			} catch (error) {
				console.error('❌ Ошибка сохранения данных в localStorage:', error)
				this.showNotification(
					'Не удалось сохранить данные. Проверьте доступное место в хранилище.',
					'error'
				)
			}
		},

		loadFromLocalStorage() {
			try {
				const storageKey = this.getStorageKey()
				let savedData = null
				try {
					savedData = localStorage.getItem(storageKey)
				} catch (error) {
					console.warn(
						`Не удалось прочитать данные модуля ${this.moduleId} из localStorage:`,
						error
					)
					return
				}

				if (!savedData) {
					console.log(`ℹ️ Нет сохраненных данных для модуля ${this.moduleId}`)
					return
				}

				let parsedData = null
				try {
					parsedData = JSON.parse(savedData)
				} catch (error) {
					console.warn(
						`Сохраненные данные модуля ${this.moduleId} повреждены, очищаем localStorage`,
						error
					)
					this.clearModuleData()
					return
				}

				// Проверяем версию данных для возможной миграции
				if (parsedData.version !== '1.0') {
					console.warn('⚠️ Обнаружена устаревшая версия данных')
					// Здесь можно добавить логику миграции данных
				}

				// Восстанавливаем данные
				if (!parsedData || typeof parsedData !== 'object') {
					console.warn(
						`Сохраненные данные модуля ${this.moduleId} имеют неверный формат, очищаем localStorage`
					)
					this.clearModuleData()
					return
				}

				if (
					parsedData.moduleId !== undefined &&
					parsedData.moduleId !== null &&
					this.moduleId !== undefined &&
					this.moduleId !== null &&
					String(parsedData.moduleId) !== String(this.moduleId)
				) {
					console.warn(
						`Сохраненные данные относятся к другому модулю (${parsedData.moduleId}), очищаем localStorage`
					)
					this.clearModuleData()
					return
				}

				// Восстанавливаем данные (строго как массивы)
				this.lessons = Array.isArray(parsedData.lessons) ? parsedData.lessons : []
				this.practices = Array.isArray(parsedData.practices)
					? parsedData.practices
					: []
				this.homeworks = Array.isArray(parsedData.homeworks)
					? parsedData.homeworks
					: []
				this.tests = Array.isArray(parsedData.tests) ? parsedData.tests : []

				console.log(
					` Данные модуля ${this.moduleId} загружены из localStorage:`,
					{
						lessons: this.lessons.length,
						practices: this.practices.length,
						homeworks: this.homeworks.length,
						tests: this.tests.length,
					}
				)

				// Показываем уведомление пользователю
				const totalItems =
					this.lessons.length +
					this.practices.length +
					this.homeworks.length +
					this.tests.length

				if (totalItems > 0) {
					this.showNotification(
						`Загружено ${totalItems} элементов модуля`,
						'success'
					)
				}
			} catch (error) {
				console.error('❌ Ошибка загрузки данных из localStorage:', error)
				this.showNotification(
					'Не удалось загрузить сохраненные данные',
					'error'
				)
			}
		},

		clearModuleData() {
			try {
				const storageKey = this.getStorageKey()
				try {
					localStorage.removeItem(storageKey)
				} catch (error) {
					console.warn(
						`Не удалось удалить данные модуля ${this.moduleId} из localStorage:`,
						error
					)
					return
				}
				console.log(`🗑️ Данные модуля ${this.moduleId} удалены из localStorage`)
			} catch (error) {
				console.error('❌ Ошибка удаления данных из localStorage:', error)
			}
		},

		//  ВСПОМОГАТЕЛЬНЫЕ МЕТОДЫ
		formatAttempts(attempts) {
			if (attempts === 1) return '1 попытка'
			if (attempts >= 2 && attempts <= 4) return `${attempts} попытки`
			return `${attempts} попыток`
		},

		getDefaultDueDate() {
			const date = new Date()
			date.setDate(date.getDate() + 7) // +7 дней
			return date.toLocaleDateString('ru-RU')
		},

		formatDate(date) {
			if (!date) return ''

			const options = {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			}

			return new Date(date).toLocaleDateString('ru-RU', options)
		},

		//  СИСТЕМА УВЕДОМЛЕНИЙ
		showNotification(message, type = 'info') {
			// Можно использовать любую библиотеку для toast уведомлений
			// Например: vue-toastification, vue-notification и т.д.
			console.log(`[${type.toUpperCase()}] ${message}`)

			// Простая реализация через alert (замените на toast)
			if (type === 'success') {
				// alert(` ${message}`)
			} else if (type === 'error') {
				alert(`❌ ${message}`)
			}
			// Для info можно не показывать alert
		},

		// ============================================
		// МЕТОДЫ ДЛЯ РАБОТЫ С LOCALSTORAGE
		// ============================================

		saveFormState(formType, isOpen, editingData = null) {
			const key = `moduleTestsSection_${this.moduleId}_${formType}FormState`
			const state = {
				isOpen,
				editingData,
				timestamp: Date.now(),
			}
			try {
				localStorage.setItem(key, JSON.stringify(state))
			} catch (error) {
				console.warn(
					`Не удалось сохранить состояние формы ${formType} в localStorage:`,
					error
				)
			}
			console.log(`💾 Сохранено состояние формы ${formType}:`, state)
		},

		restoreFormStates() {
			const formTypes = ['lesson', 'test', 'practice', 'homework']

			formTypes.forEach(formType => {
				const key = `moduleTestsSection_${this.moduleId}_${formType}FormState`
				let savedState = null
				try {
					savedState = localStorage.getItem(key)
				} catch (error) {
					console.warn(
						`Не удалось прочитать состояние формы ${formType} из localStorage:`,
						error
					)
					return
				}

				if (savedState) {
					try {
						const state = JSON.parse(savedState)
						// Проверяем, что состояние не старше 24 часов
						const isExpired = Date.now() - state.timestamp > 24 * 60 * 60 * 1000

						if (!isExpired && state.isOpen) {
							console.log(
								`🔄 Восстановление состояния формы ${formType}:`,
								state
							)

							// Восстанавливаем состояние формы
							switch (formType) {
								case 'lesson':
									this.showLessonCreation = true
									this.editingMaterial = state.editingData
									this.editingMaterialType = 'lessons'
									this.lessonFormKey++
									break
								case 'test':
									this.showTestCreation = true
									this.editingMaterial = state.editingData
									this.editingMaterialType = 'tests'
									this.testFormKey++
									break
								case 'practice':
									this.showPracticeCreation = true
									this.editingMaterial = state.editingData
									this.editingMaterialType = 'practice'
									this.practiceFormKey++
									break
								case 'homework':
									this.showHomeworkCreation = true
									this.editingMaterial = state.editingData
									this.editingMaterialType = 'homework'
									this.homeworkFormKey++
									break
							}
						} else if (isExpired) {
							// Удаляем устаревшее состояние
							try {
								localStorage.removeItem(key)
							} catch (error) {
								console.warn(
									`Не удалось удалить устаревшее состояние формы ${formType} из localStorage:`,
									error
								)
							}
							console.log(`🗑️ Удалено устаревшее состояние формы ${formType}`)
						}
					} catch (error) {
						console.error(
							`❌ Ошибка восстановления состояния формы ${formType}:`,
							error
						)
						try {
							localStorage.removeItem(key)
						} catch (error) {
							console.warn(
								`Не удалось удалить поврежденное состояние формы ${formType} из localStorage:`,
								error
							)
						}
					}
				}
			})
		},

		clearFormState(formType) {
			const key = `moduleTestsSection_${this.moduleId}_${formType}FormState`
			try {
				localStorage.removeItem(key)
			} catch (error) {
				console.warn(
					`Не удалось очистить состояние формы ${formType} в localStorage:`,
					error
				)
			}
			console.log(`🗑️ Очищено состояние формы ${formType}`)
		},
	},
}
</script>

<style scoped>
/*  Анимация подсветки при скролле */
@keyframes mts-highlight-pulse {
	0% {
		opacity: 0;
	}
	40% {
		opacity: 1;
	}
	70% {
		opacity: 0.35;
	}
	100% {
		opacity: 0;
	}
}

.mts-highlight-animation {
	position: relative;
	z-index: 10;
}

/* Альтернативный вариант - подсветка border */
.mts-highlight-animation > * {
	position: relative;
}

.mts-highlight-animation > *::after {
	content: '';
	position: absolute;
	inset: 0;
	border-radius: inherit;
	pointer-events: none;
	box-shadow: inset 0 0 0 2px rgba(0, 149, 236, 0.9);
	opacity: 0;
	animation: mts-highlight-pulse 0.6s ease-out 2;
}

/* ============================================
   MAIN CONTAINER
   ============================================ */
.mts-container {
	display: flex;
	flex-direction: column;
	gap: 26px;
	min-width: 100%;
	position: relative;
	top: -300px;
}

/* ============================================
   TABS NAVIGATION
   ============================================ */
.mts-tabs {
	display: flex;
	gap: 19px;
	margin-left: 28px;
	width: fit-content;
}

.mts-tabs__item {
	align-items: center;
	background-color: #f5f5f5;
	border: none;
	border-radius: 12px;
	color: rgba(0, 0, 0, 0.6);
	cursor: pointer;
	display: flex;
	font-family: 'Poppins', Helvetica;
	font-size: 20px;
	font-weight: 700;
	height: 50px;
	justify-content: center;
	letter-spacing: 0;
	line-height: normal;
	padding: 0 20px;
	transition: all 0.3s ease;
	white-space: nowrap;
}

.mts-tabs__item:hover {
	background-color: #e8e8e8;
	color: rgba(0, 0, 0, 0.8);
}

.mts-tabs__item--active {
	background-color: rgba(0, 149, 236, 0.8);
	color: #ffffff;
}

.mts-tabs__item--active:hover {
	background-color: #0095ec;
}

/* Specific tab widths */
.mts-tabs__item:nth-child(1) {
	width: 156px;
}

.mts-tabs__item:nth-child(2) {
	width: 312px;
}

.mts-tabs__item:nth-child(3) {
	width: 260px;
}

.mts-tabs__item:nth-child(4) {
	width: 156px;
}

.mts-tabs__item:nth-child(5) {
	width: 180px;
}

/* ============================================
   CONTENT AREA
   ============================================ */
.mts-content {
	min-height: 100px; /* Компактный минимальный размер */
	position: relative;
	width: 100%;
	display: flex;
	flex-direction: column;
}

.mts-content__bg {
	background-color: #f5f5f5;
	border: 0.5px solid #d6d6d6;
	border-radius: 8px;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	width: 100%;
	height: 100%;
	z-index: 0;
}

/* ============================================
   HEADER SECTION
   ============================================ */
.mts-header {
	align-items: center;
	display: flex;
	gap: 80px;
	justify-content: center;
	padding: 10px 0;
	position: relative;
	z-index: 2;
}

.mts-header__title {
	color: #000000;
	font-family: 'Poppins', Helvetica;
	font-size: 30px;
	font-weight: 700;
	letter-spacing: 0;
	line-height: 54px;
	margin: 0;
	white-space: nowrap;
}

.mts-header__create-btn {
	background: transparent;
	border: 1px solid #32cd32;
	border-radius: 5px;
	cursor: pointer;
	height: 30px;
	min-width: 200px;
	padding: 0 15px;
	position: relative;
	transition: all 0.2s ease;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.mts-header__create-btn:hover {
	transform: translateY(-1px);
	background-color: rgba(50, 205, 50, 0.05);
	box-shadow: 0 2px 4px rgba(50, 205, 50, 0.15);
}

.mts-header__create-btn:active {
	transform: translateY(0);
	box-shadow: 0 1px 2px rgba(50, 205, 50, 0.1);
}

.mts-header__create-bg {
	/* Удален - больше не нужен, так как стили применены к самой кнопке */
	display: none;
}

.mts-header__create-text {
	color: #32cd32;
	font-family: 'Poppins', Helvetica;
	font-size: 15px;
	font-weight: 600;
	letter-spacing: 0.3px;
	line-height: normal;
	white-space: nowrap;
	position: relative;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.mts-empty {
	align-items: center;
	display: flex;
	justify-content: center;
	min-height: 150px; /* Уменьшен для компактности */
	padding: 40px 20px; /* Добавлен padding для лучшего отображения */
	position: relative;
	z-index: 1;
}

.mts-empty__placeholder {
	color: rgba(0, 0, 0, 0.15);
	font-family: 'Poppins', Helvetica;
	font-size: 100px;
	font-weight: 800;
	letter-spacing: 0;
	line-height: normal;
	text-align: center;
}

/* ============================================
   MATERIALS LIST (карточки созданных материалов)
   ============================================ */
.mts-materials {
	padding: 40px 60px 40px; /* Уменьшен верхний padding для компактности */
	position: relative;
	z-index: 1;
	max-width: 100%; /* Ограничиваем ширину контейнера */
}

/* Ограничиваем ширину слайдера, чтобы карточки не выходили за пределы */
.mts-materials :deep(.materials-slider) {
	max-width: 100%;
	overflow: visible; /* Карточки видны полностью */
}

.mts-materials :deep(.materials-slider__wrapper) {
	max-width: 100%;
	overflow: hidden; /* Скрываем только то что за пределами wrapper */
}

.mts-materials :deep(.materials-slider__track),
.mts-materials :deep(.materials-slider__grid) {
	overflow: visible !important;
}

/* ============================================
   TESTS LIST
   ============================================ */
.mts-tests {
	display: grid;
	gap: 20px;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	padding: 80px 60px 40px;
	position: relative;
	z-index: 1;
}

.mts-test-card {
	background-color: #ffffff;
	border: 1px solid rgba(0, 0, 0, 0.1);
	border-radius: 12px;
	cursor: pointer;
	padding: 20px;
	transition: all 0.3s ease;
}

.mts-test-card:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.mts-test-card__title {
	color: #000000;
	font-family: 'Poppins', Helvetica;
	font-size: 20px;
	font-weight: 700;
	letter-spacing: 0;
	line-height: 1.4;
	margin: 0 0 10px 0;
}

.mts-test-card__description {
	color: rgba(0, 0, 0, 0.6);
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 400;
	letter-spacing: 0;
	line-height: 1.5;
	margin: 0 0 15px 0;
}

.mts-test-card__meta {
	color: rgba(0, 0, 0, 0.5);
	display: flex;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 400;
	gap: 15px;
	letter-spacing: 0;
}

/* ============================================
   DRAFTS SECTION
   ============================================ */
.mts-drafts {
	padding: 20px 62px;
	position: relative;
	width: 100%;
	z-index: 1;
	margin-top: 40px;
}

.mts-drafts__header {
	margin-bottom: 20px;
	position: relative;
}

.mts-drafts__title {
	color: #000000;
	font-family: 'Poppins', Helvetica;
	font-size: 25px;
	font-weight: 700;
	letter-spacing: 0;
	line-height: 45px;
	margin: 0;
	position: relative;
	display: inline-block;
	white-space: nowrap;
}

.mts-drafts__divider {
	background-color: #b4b4b4;
	height: 1px;
	position: absolute;
	left: 172px;
	top: 23px;
	width: calc(100% - 172px);
}

/* Drafts Slider */
.mts-drafts__slider {
	margin-top: 30px;
	overflow: visible; /*  Добавлено */
}

.mts-drafts__slider :deep(.materials-slider),
.mts-drafts__slider :deep(.materials-slider__wrapper),
.mts-drafts__slider :deep(.materials-slider__track),
.mts-drafts__slider :deep(.materials-slider__grid) {
	overflow: visible !important;
}

.mts-settings {
	padding: 24px 60px 40px;
	position: relative;
	z-index: 1;
}

/* Увеличиваем padding для MaterialCard чтобы тень не обрезалась */
.mts-materials [data-material-id],
.mts-drafts__slider [data-material-id] {
	padding: 8px; /*  Добавлено - даёт пространство для тени */
	margin: -8px; /*  Компенсируем padding чтобы не увеличивать размер */
}

/* ============================================
   RESPONSIVE DESIGN
   ============================================ */
@media (max-width: 1400px) {
	.mts-tabs {
		margin-left: 15px;
	}

	.mts-header {
		flex-direction: column;
		gap: 20px;
		padding: 20px;
	}

	.mts-drafts {
		padding: 20px;
	}

	.mts-drafts__divider {
		left: 150px;
		width: calc(100% - 150px);
	}
}

/* ============================================
   CREATION FORMS
   ============================================ */
.mts-test-creation,
.mts-lesson-creation,
.mts-practice-creation,
.mts-homework-creation {
	margin-top: 30px;
	position: relative;
	z-index: 2;
}

@media (max-width: 1024px) {
	.mts-tabs {
		flex-wrap: wrap;
		gap: 10px;
		margin-left: 10px;
	}

	.mts-tabs__item {
		font-size: 16px;
		height: 45px;
		width: auto !important;
	}

	.mts-header__title {
		font-size: 24px;
		line-height: 1.4;
	}

	.mts-materials {
		padding: 40px 20px 20px; /* Уменьшен верхний padding */
	}

	.mts-settings {
		padding: 20px;
	}

	.mts-tests {
		grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
		padding: 40px 20px 20px; /* Уменьшен верхний padding */
	}

	.mts-drafts {
		padding: 20px;
		margin-top: 20px;
	}
}

@media (max-width: 768px) {
	.mts-container {
		gap: 15px;
	}

	.mts-tabs {
		gap: 8px;
		margin-left: 5px;
	}

	.mts-tabs__item {
		font-size: 14px;
		height: 40px;
		padding: 0 15px;
	}

	.mts-content {
		min-height: 250px; /* Компактный размер для планшетов */
	}

	.mts-header {
		align-items: stretch;
		padding: 15px;
	}

	.mts-header__title {
		font-size: 20px;
		text-align: center;
	}

	.mts-header__create-btn {
		width: 100%;
		justify-content: center;
	}

	.mts-header__create-bg {
		width: 100%;
	}

	.mts-empty__placeholder {
		font-size: 60px;
	}

	.mts-materials {
		padding: 30px 15px 15px; /* Уменьшен верхний padding */
	}

	.mts-settings {
		padding: 15px;
	}

	.mts-tests {
		grid-template-columns: 1fr;
		padding: 30px 15px 15px; /* Уменьшен верхний padding */
	}

	.mts-test-card__title {
		font-size: 18px;
	}

	.mts-test-card__description {
		font-size: 13px;
	}

	.mts-test-card__meta {
		font-size: 12px;
	}

	.mts-drafts {
		padding: 15px;
		margin-top: 20px;
	}

	.mts-drafts__title {
		font-size: 20px;
		line-height: 1.4;
		position: relative;
	}

	.mts-drafts__divider {
		display: none;
	}

	.mts-drafts__placeholder {
		font-size: 50px;
	}

	.mts-drafts__list {
		grid-template-columns: 1fr;
		margin-top: 20px;
	}

	.mts-draft-card {
		padding: 12px;
	}

	.mts-draft-card__title {
		font-size: 14px;
	}

	.mts-draft-card__date {
		font-size: 11px;
	}
}

@media (max-width: 480px) {
	.mts-tabs__item {
		font-size: 12px;
		height: 35px;
		padding: 0 10px;
	}

	.mts-header__title {
		font-size: 18px;
	}

	.mts-header__create-btn {
		height: 40px;
	}

	.mts-header__create-bg {
		height: 40px;
	}

	.mts-header__create-text {
		font-size: 14px;
	}

	.mts-empty__placeholder {
		font-size: 40px;
	}

	.mts-drafts__placeholder {
		font-size: 40px;
	}
}

/* ============================================
   ACCESSIBILITY
   ============================================ */
.mts-tabs__item:focus,
.mts-header__create-btn:focus,
.mts-test-card:focus,
.mts-draft-card:focus {
	outline: 2px solid #0095ec;
	outline-offset: 2px;
}

.mts-tabs__item:focus-visible,
.mts-header__create-btn:focus-visible,
.mts-test-card:focus-visible,
.mts-draft-card:focus-visible {
	outline: 2px solid #0095ec;
	outline-offset: 2px;
}

/* ============================================
   LOADING STATE
   ============================================ */
.mts-loading {
	align-items: center;
	display: flex;
	justify-content: center;
	min-height: 200px;
	position: relative;
	z-index: 1;
}

.mts-loading__spinner {
	animation: mts-spin 1s linear infinite;
	border: 3px solid rgba(0, 149, 236, 0.2);
	border-radius: 50%;
	border-top-color: #0095ec;
	height: 40px;
	width: 40px;
}

@keyframes mts-spin {
	to {
		transform: rotate(360deg);
	}
}

/* ============================================
   ERROR STATE
   ============================================ */
.mts-error {
	align-items: center;
	background-color: rgba(244, 40, 40, 0.05);
	border: 1px solid rgba(244, 40, 40, 0.2);
	border-radius: 8px;
	color: #f42828;
	display: flex;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	justify-content: center;
	margin: 20px;
	padding: 15px;
	position: relative;
	z-index: 1;
}

.mts-error__icon {
	margin-right: 10px;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.mts-fade-enter-active,
.mts-fade-leave-active {
	transition: opacity 0.3s ease;
}

.mts-fade-enter-from,
.mts-fade-leave-to {
	opacity: 0;
}

.mts-slide-enter-active,
.mts-slide-leave-active {
	transition: all 0.3s ease;
}

.mts-slide-enter-from {
	opacity: 0;
	transform: translateY(-10px);
}

.mts-slide-leave-to {
	opacity: 0;
	transform: translateY(10px);
}

/* ============================================
   TEST CREATION SECTION
   ============================================ */
.mts-test-creation {
	margin-top: 30px;
	padding: 0;
	position: relative;
	width: 100%;
	z-index: 1;
}

/* ============================================
   PRINT STYLES
   ============================================ */
@media print {
	.mts-tabs,
	.mts-header__create-btn {
		display: none;
	}

	.mts-content__bg {
		border: none;
	}

	.mts-test-card,
	.mts-draft-card {
		break-inside: avoid;
		page-break-inside: avoid;
	}
}
</style>
